import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useProjectAuth } from './useProjectAuth'
import * as XLSX from 'xlsx'
import { buildCumulativeData, buildCostControlDataFromRelease, resolveCurrentForecastState, applyLiveForecastToCumulative } from '../lib/buildCostControlData'
import { exportCostControlPdf } from '../lib/exportCostControlPdf'
import { buildMonthlyActualCostData } from '../lib/monthlyActualCost'

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const YEARS = Array.from({length: 10}, (_, i) => 2020 + i)

export default function CostControl({ projectId: initProjectId, initialReleaseId, onInitialReleaseConsumed }) {
  const [projects, setProjects] = useState([])
  const [projectId, setProjectId] = useState(null)
  const { can } = useProjectAuth(projectId)
  useEffect(() => { if (initProjectId) setProjectId(initProjectId) }, [initProjectId])
  const [projectName, setProjectName] = useState('')
  const [orgName, setOrgName] = useState('')
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  // ─── Report Context (agreed explicitly) ─────────────────────────────────
  // Cost Control answers one business question — "show me the financial
  // position of the project" — and the ONLY thing that changes is which
  // financial STATE is being asked about. reportContext is the single
  // source of truth for that: 'live' reads buildCumulativeData() (current
  // operational state); 'release' reads buildCostControlDataFromRelease()
  // for a specific, explicitly-selected, immutable Project Forecast
  // Release (approved historical state). Kept as one object (not two
  // separate state variables) so the page stays independent of
  // implementation details and can extend to future report sources
  // (Budget Baseline, Tender Baseline, etc.) without a state shape change.
  // Default is 'live' — explicit agreement, for backward compatibility and
  // to avoid silently changing existing user expectations; switching to a
  // Release is always a deliberate user action, never automatic.
  const [reportContext, setReportContext] = useState({ source: 'live', releaseId: null })
  const [availableReleases, setAvailableReleases] = useState([]) // releases for the current project, loaded once
  const [showPeriodModal, setShowPeriodModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [reportData, setReportData] = useState([])
  const [expandedCodes, setExpandedCodes] = useState(new Set())
  const [prevActualOverrides, setPrevActualOverrides] = useState({})
  const [showAll, setShowAll] = useState(false)
  const [collapseDetail, setCollapseDetail] = useState(false)
  const [filterCode, setFilterCode] = useState('')
  const [summary, setSummary] = useState({
    totalBudget: 0, totalCommitment: 0, totalActual: 0,
    totalAccrual: 0, totalCashOut: 0, totalEAC: 0
  })
  // monthlyActualCost — buildMonthlyActualCostData()'s output, the SAME
  // shared Movement source Executive Report and the Cost Control PDF
  // already read from. Loaded here so the screen's own Executive Cards
  // and category-level (Direct/Indirect) Summary rows can show "This
  // Period" without any separate calculation — a single lookup by code
  // into this object, exactly as buildCostControlPdfModel.js already
  // does. Not derived from reportData/buildCumulativeData() at all.
  const [monthlyActualCost, setMonthlyActualCost] = useState(null)
  const [contractValue, setContractValue] = useState(0)
  const [visibleCols, setVisibleCols] = useState({
    commitment: true, accrual: true,
    varEAC: false, ev: false, cpi: false, costVariance: false
  })
  const [colPanelOpen, setColPanelOpen] = useState(false)
  const theadRef = useRef(null)
  const [theadHeight, setTheadHeight] = useState(38) // fallback matches old hardcoded value until measured
  const [overBudgetOnly, setOverBudgetOnly] = useState(false)
  const [flagged, setFlagged] = useState(() => {
    try { const s = localStorage.getItem('cc_flagged'); return s ? new Set(JSON.parse(s)) : new Set() } catch { return new Set() }
  })
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false)
  const [drillRow, setDrillRow] = useState(null)
  const [activeTab, setActiveTab] = useState('report') // 'report' | 'activity'
  const [activityAnalysisTab, setActivityAnalysisTab] = useState('cost_code') // 'cost_code' | 'resource' — separate tabs per analysis mode (explicitly requested), so Cost Code Activities and Resource Activities are never listed in the same table
  // Direct Works is split into sub-tabs by Resource Type. Indirect stays single-level for now.
  const [directResourceTab, setDirectResourceTab] = useState('material') // 'material' | 'labour' | 'subcontract' | 'equipment' | 'summary'
  const [activities, setActivities] = useState([])
  const [actEditOpen, setActEditOpen] = useState(false)
  const [actForm, setActForm] = useState({ id: null, name: '', costCodeIds: [], analysisMode: 'cost_code' })
  const [actLoading, setActLoading] = useState(false)
  const [actColsOpen, setActColsOpen] = useState(false)
  const [expandedActivities, setExpandedActivities] = useState(new Set())
  const [actVisibleCols, setActVisibleCols] = useState({
    bac: true, commitment: true, actual: true, accrual: false,
    cashOut: false, eac: true, varComp: true, thisPeriod: false
  })

  const loadActivities = useCallback(async (pid) => {
    if (!pid) return
    setActLoading(true)
    const { data } = await supabase.from('activity_reports').select('*').eq('project_id', pid).order('created_at')
    setActivities((data || []).map(a => ({ id: a.id, name: a.name, costCodeIds: a.cost_code_ids || [], analysisMode: a.analysis_mode || 'cost_code' })))
    setActLoading(false)
  }, [])

  async function saveActivity(actForm) {
    if (!projectId) return
    if (actForm.id) {
      await supabase.from('activity_reports').update({ name: actForm.name, cost_code_ids: actForm.costCodeIds, analysis_mode: actForm.analysisMode }).eq('id', actForm.id)
    } else {
      await supabase.from('activity_reports').insert({ project_id: projectId, name: actForm.name, cost_code_ids: actForm.costCodeIds, analysis_mode: actForm.analysisMode })
    }
    loadActivities(projectId)
  }

  async function deleteActivity(id) {
    await supabase.from('activity_reports').delete().eq('id', id)
    setActivities(prev => prev.filter(a => a.id !== id))
  }

  // flattenAllLevels() — SHARED helper (explicitly requested): the single
  // place that knows how to enumerate every node in the Cost Code /
  // Resource / Sub Code hierarchy buildCumulativeData() and
  // buildCostControlDataFromRelease() both produce (Cost Code rows with
  // resourceRows[] and children[] nested inside them). Used by
  // computeActivityRow() (Activity Report) and exportToExcel() — any
  // future feature that needs "every node, regardless of level" (a
  // future PDF export, a global search, a future report) should reuse
  // this rather than re-implementing its own partial walk, which is
  // exactly how exportToExcel() previously missed every Resource row
  // (it only walked parent.children, never parent.resourceRows).
  function flattenAllLevels(parents) {
    const out = []
    for (const parent of parents) {
      out.push(parent)
      for (const res of (parent.resourceRows || [])) {
        out.push(res)
      }
      for (const child of (parent.children || [])) {
        out.push(child)
      }
    }
    return out
  }

  // buildNodeChildrenMap() — builds a lookup of { nodeId: [direct child
  // nodes] } across the FULL Cost Code -> Resource -> Sub Code hierarchy.
  // Used by expandToLeaves() below so that selecting a Cost Code or
  // Resource in an Activity automatically includes everything beneath it
  // — restoring the original design intent (this modal was built when
  // Cost Code was the only level, so selecting one "just worked" by
  // implicitly including its Sub Codes; the Resource level was added
  // later without updating this behavior, so selecting a Resource today
  // stopped at itself instead of also reaching its own Sub Codes).
  function buildNodeChildrenMap(parents) {
    const map = {}
    for (const parent of parents) {
      const resIds = (parent.resourceRows || []).map(r => r.id)
      const orphanIds = (parent.children || []).filter(c => !c.isUnderResource).map(c => c.id)
      map[parent.id] = [...resIds, ...orphanIds]
      for (const res of (parent.resourceRows || [])) {
        const subIds = (parent.children || []).filter(c => c.isUnderResource && c.parent_id === res.id).map(c => c.id)
        map[res.id] = subIds
      }
    }
    return map
  }

  // expandToLeaves() — given the user's selected ids, returns the set of
  // LEAF-level ids (Sub Codes, or a Resource/Cost Code with no children of
  // its own) that should actually be summed. Expanding to leaves — rather
  // than summing a selected parent AND its descendants together — avoids
  // double-counting: a Resource's own bac/eac already includes its Sub
  // Codes' contribution once a Draft/Release resolves it, so summing both
  // the Resource row and its Sub Code rows would count that contribution
  // twice.
  function expandToLeaves(selectedIds, childrenMap) {
    const leaves = new Set()
    function visit(id) {
      const kids = childrenMap[id]
      if (!kids || kids.length === 0) { leaves.add(id); return }
      for (const kid of kids) visit(kid)
    }
    for (const id of selectedIds) visit(id)
    return leaves
  }

  // computeActivityRow() — works identically for BOTH analysis modes,
  // because expandToLeaves() is already mode-agnostic: given a Cost Code
  // id it walks down through its Resources to their Sub Codes; given a
  // Resource id it walks down to that Resource's own Sub Codes only,
  // never reaching a sibling Resource or the parent Cost Code. This is
  // exactly the isolation the two-mode design requires (explicitly
  // agreed) — Cost Code Activities and Resource Activities never mix
  // levels, because each Activity's costCodeIds are homogeneous by
  // construction (enforced by the two-tab selection UI, not by this
  // function).
  //
  // BAC SPECIAL CASE: Budget lives on the Resource itself, not on its Sub
  // Codes (Sub Codes always carry bac=0 by design). Expanding a selected
  // Resource down to its Sub Code leaves for Commitment/Actual/EAC is
  // correct (those figures are genuinely resolved bottom-up), but doing
  // the same for BAC would silently lose the only real Budget figure
  // available. So BAC is summed from the ORIGINALLY SELECTED ids
  // directly (before leaf-expansion), not from the expanded leaf set —
  // every other field is summed from the expanded leaves.
  function computeActivityRow(costCodeIds) {
    const parents = [...(reportData.directRows || []), ...(reportData.indirectRows || [])]
    const all = flattenAllLevels(parents)
    const childrenMap = buildNodeChildrenMap(parents)
    const leafIds = expandToLeaves(costCodeIds, childrenMap)
    const matched = all.filter(r => leafIds.has(r.id))
    const selectedNodes = all.filter(r => costCodeIds.includes(r.id))
    const sum = (key) => matched.reduce((s, r) => s + (r[key] || 0), 0)
    const bacSum = selectedNodes.reduce((s, r) => s + (r.bac || 0), 0)
    return {
      bac: bacSum, commitment: sum('commitment'), actual: sum('totalActualCost'),
      accrual: sum('accrual'), cashOut: sum('cashOut'), eac: sum('eac'),
      thisPeriod: sum('actualThisPeriod'),
      varComp: bacSum - sum('eac'),
      costCodes: matched.map(r => ({ code: r.code, description: r.description, bac: r.bac, commitment: r.commitment, actual: r.totalActualCost, accrual: r.accrual, cashOut: r.cashOut, eac: r.eac, thisPeriod: r.actualThisPeriod, varComp: (r.bac||0)-(r.eac||0) }))
    }
  }

  useEffect(() => { fetchProjects() }, [])

  // Populates projectName/projectOrg/projectOrgId/contractValue whenever
  // projectId is set from ANY source — not just the manual project-picker
  // click (line ~657, which sets these directly from the clicked row and
  // still works unchanged). This covers the "Open Release" / initProjectId
  // navigation path, which only ever sets projectId itself (see the
  // initProjectId effect above) and previously left these fields blank —
  // causing the PDF header's Project/Organization to render empty for
  // that entry path. Reads only from `projects` (already fetched by
  // fetchProjects() above) — no new query, same fields fetchProjects()
  // already selects (id, name, contract_value, organization_id,
  // organizations(name)).
  useEffect(() => {
    if (!projectId || projects.length === 0) return
    const p = projects.find(pr => pr.id === projectId)
    if (!p) return
    setProjectName(p.name || '')
    setProjectOrg(p.organizations?.name || '')
    setProjectOrgId(p.organization_id || null)
    setContractValue(p.contract_value || 0)
  }, [projectId, projects]) // eslint-disable-line
  useEffect(() => {
    if (!projectId) return
    supabase.from('projects').select('cost_control_selected_month, cost_control_selected_year').eq('id', projectId).single()
      .then(({ data }) => {
        if (data?.cost_control_selected_month) setSelectedMonth(data.cost_control_selected_month)
        if (data?.cost_control_selected_year) setSelectedYear(data.cost_control_selected_year)
      })
  }, [projectId])
  useEffect(() => { if (projectId) loadAvailableReleases(projectId) }, [projectId]) // eslint-disable-line

  // Release Management's "Open in Cost Control" action (initialReleaseId) —
  // applied once, as soon as the Release list has loaded, then consumed
  // (onInitialReleaseConsumed) so a later normal visit to this page never
  // re-applies a stale selection from a previous navigation.
  useEffect(() => {
    if (!initialReleaseId || availableReleases.length === 0) return
    const match = availableReleases.find(r => r.id === initialReleaseId)
    if (match) {
      setReportContext({ source: 'release', releaseId: initialReleaseId })
    }
    onInitialReleaseConsumed?.()
  }, [initialReleaseId, availableReleases]) // eslint-disable-line
  useEffect(() => { if (projectId) buildReport() }, [projectId, selectedMonth, selectedYear, reportContext]) // eslint-disable-line
  useEffect(() => { if (projectId) loadActivities(projectId) }, [projectId]) // eslint-disable-line

  async function fetchProjects() {
    const { data } = await supabase.from('projects').select('id, name, contract_value, organization_id, organizations(name)').order('name')
    setProjects(data || [])
  }

  // Loads every Project Forecast Release for this project (for the Release
  // picker), newest first — a plain read, no report data computed here.
  // Loads every Project Forecast Release summary for this project (for the
  // Release picker), newest first. Consumes the shared
  // project_forecast_release_summary view (ADD_Forecast_Architecture.md
  // SS17/SS19 — SSOT) rather than rebuilding the Direct/Indirect
  // revision-number join here — Executive Report, Dashboard, and any
  // future consumer needing the same summary should query the same view.
  async function loadAvailableReleases(pid) {
    const { data } = await supabase
      .from('project_forecast_release_summary')
      .select('release_id, project_id, forecast_date, release_number, direct_snapshot_id, direct_revision_number, indirect_snapshot_id, indirect_revision_number, notes, created_at, is_current')
      .eq('project_id', pid)
      .order('forecast_date', { ascending: false })
      .order('release_number', { ascending: false })

    // Map release_id -> id to match the shape the rest of this component
    // already expects (release.id), without renaming the view's column.
    const releases = (data || []).map(r => ({ ...r, id: r.release_id }))
    setAvailableReleases(releases)
  }

  async function buildReport(pid) {
    // Load contract value if not set
    if (!contractValue && (pid || projectId)) {
      const pId = pid || projectId
      const proj = projects.find(p => p.id === pId)
      if (proj?.contract_value) setContractValue(proj.contract_value)
    }
    // Always fetch contract_value fresh from DB
    const pId = pid || projectId
    if (pId) {
      supabase.from('projects').select('contract_value').eq('id', pId).single()
        .then(({ data }) => { if (data?.contract_value) setContractValue(data.contract_value) })
    }
    // Load Movement ("This Period") data — the exact same shared source
    // Executive Report and the Cost Control PDF already read from
    // (buildMonthlyActualCostData()). Fired alongside the main report
    // build, not awaited before it, so a slower Movement query never
    // delays the Cost Code tree itself from rendering. This is display
    // data only — the Executive Cards' EAC/BAC/Actual/etc figures below
    // never depend on it.
    if (pId) {
      buildMonthlyActualCostData(pId).then(setMonthlyActualCost).catch(() => setMonthlyActualCost(null))
    }
    const id = pid || projectId
    if (!id) return
    setLoading(true)
    try {
      // ─── Report Context branch (explicitly agreed design) ──────────────
      // Cost Control answers one question — "the financial position of the
      // project" — reportContext.source decides which financial STATE:
      //   'live'    → buildCumulativeData() — current operational state.
      //   'release' → buildCostControlDataFromRelease() — the explicitly
      //               selected, immutable, approved historical Release.
      // Everything downstream (tabs, layout, drill-downs, exports) behaves
      // identically regardless of which branch ran, since both return the
      // exact same output shape.
      let result
      if (reportContext.source === 'release') {
        const release = availableReleases.find(r => r.id === reportContext.releaseId)
          || (reportContext.releaseId ? (await supabase.from('project_forecast_releases').select('*').eq('id', reportContext.releaseId).single()).data : null)
        if (!release) {
          setReportData({ directRows: [], indirectRows: [], directTotals: {}, indirectTotals: {}, grandTotals: {},
            resourceTypeTotals: {}, indirectCategories: [], uncategorizedIndirectRows: [], uncategorizedTotals: {} })
          setSummary({ totalBudget: 0, totalCommitment: 0, totalActual: 0, totalAccrual: 0, totalCashOut: 0, totalEAC: 0 })
          setLoading(false)
          return
        }
        result = await buildCostControlDataFromRelease(release)
        // Explicit availability flags — distinguishes "no snapshot
        // attached to this Release" (a real Release-composition fact,
        // per SS17.2's agreed "at least one side" validation) from "a
        // snapshot exists but happens to have zero rows/values" (an
        // ordinary empty report). These must never look the same to the
        // user (explicitly requested).
        result.hasDirectSnapshot = release.direct_snapshot_id != null
        result.hasIndirectSnapshot = release.indirect_snapshot_id != null
      } else {
        // Live: BAC/Commitment/Actual/etc. still come from
        // buildCumulativeData() (unaffected — that part was never in
        // question). EAC, however, is overlaid from
        // resolveCurrentForecastState() — the exact same resolution
        // Direct/Indirect Forecast display — instead of
        // buildCumulativeData()'s own internal resolveForecastOwnership(),
        // which was found to silently collapse a Resource's EAC to 0 when
        // the Resource itself had no forecast but its (always-zero-budget)
        // Sub Codes were treated as aggregation owners. Per this
        // conversation's conclusion: the fix is eliminating that second,
        // independent resolution path entirely — not patching it — so
        // Cost Control (Live) and Direct/Indirect Forecast always agree,
        // by construction, on "what is the effective EAC right now."
        result = await buildCumulativeData(id, selectedMonth, selectedYear)
        const [resolvedDirect, resolvedIndirect] = await Promise.all([
          resolveCurrentForecastState(id, 'direct'),
          resolveCurrentForecastState(id, 'indirect'),
        ])
        // #8: the Draft overlay and everything that depends on it (row-level variances, totals, resource-type
        // and category totals, gross profit) is done by ONE shared function in buildCostControlData.js — the same one
        // Executive Report (Live) and Dashboard now use — instead of being re-implemented inside this page.
        result = applyLiveForecastToCumulative(result, resolvedDirect, resolvedIndirect)
      }

      const { directRows, indirectRows, directTotals, indirectTotals, grandTotals,
        resourceTypeTotals, indirectCategories, uncategorizedIndirectRows, uncategorizedTotals,
        hasDirectSnapshot, hasIndirectSnapshot } = result

      setReportData({ directRows, indirectRows, directTotals, indirectTotals, grandTotals, resourceTypeTotals,
        indirectCategories, uncategorizedIndirectRows, uncategorizedTotals,
        hasDirectSnapshot: hasDirectSnapshot ?? true, hasIndirectSnapshot: hasIndirectSnapshot ?? true })

      setSummary({
        totalBudget: grandTotals.bac || 0,
        totalCommitment: grandTotals.commitment || 0,
        totalActual: grandTotals.totalActualCost || 0,
        totalAccrual: grandTotals.accrual || 0,
        totalCashOut: grandTotals.cashOut || 0,
        totalEAC: grandTotals.eac || 0,
      })

      // Expand all parents by default
      setExpandedCodes(new Set([...directRows.map(p => p.id), ...indirectRows.map(p => p.id)]))

    } catch (err) {
      console.error(err)
    }
    setLoading(false)
  }

  function toggleExpand(id) {
    setExpandedCodes(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  function exportToExcel() {
    const rows = []
    const addRow = (r, isParent, isTotal, isResource) => {
      rows.push({
        'Cost Code': isTotal ? 'TOTAL' : r.code,
        'Description': r.description || '',
        'Level': isParent ? 'Parent' : isTotal ? 'Total' : isResource ? 'Resource' : 'Sub',
        'BAC': r.bac || 0,
        'Commitment': r.commitment || 0,
        'Accrual': r.accrual || 0,
        'Invoiced (Actual)': r.actualTotal || 0,
        'Total Actual Cost': r.totalActualCost || 0,
        'Prev Actual': r.actualPrevious || 0,
        'This Period': r.actualThisPeriod || 0,
        'Cash Out': r.cashOut || 0,
        'EAC': r.eac || 0,
        'Prev EAC': r.prevEac || 0,
        'Variance EAC': r.varianceEac || 0,
        'Variance at Completion': r.varianceCompletion || 0,
        'Earned Value': r.earnedValue || 0,
        'CPI': r.cpi || 0,
      })
    }

    if (reportData.directRows) {
      // Uses the shared flattenAllLevels() helper — this previously walked
      // only parent.children, silently omitting every Resource row
      // (parent.resourceRows) from the exported file entirely. Marking
      // Resource rows explicitly ('Resource' level) so the export
      // distinguishes them from Sub Codes, matching what the on-screen
      // table already shows.
      function addHierarchy(parents) {
        for (const parent of parents) {
          addRow(parent, true, false, false)
          for (const res of (parent.resourceRows || [])) {
            addRow({ ...res, description: res.description || res.code }, false, false, true)
          }
          for (const child of (parent.children || [])) {
            addRow(child, false, false, false)
          }
        }
      }
      addHierarchy(reportData.directRows)
      addRow({ description: 'Direct Works Total', ...reportData.directTotals }, false, true)
      addHierarchy(reportData.indirectRows || [])
      addRow({ description: 'Indirect Works Total', ...reportData.indirectTotals }, false, true)
      addRow({ description: 'GRAND TOTAL', ...reportData.grandTotals }, false, true)
    }

    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, `Control Report ${MONTHS[selectedMonth-1]} ${selectedYear}`)
    XLSX.writeFile(wb, `cost_control_${selectedMonth}_${selectedYear}.xlsx`)
  }

  const [projectOrg, setProjectOrg] = useState('')
  const [projectOrgId, setProjectOrgId] = useState(null)

  // printCurrentScreenView() — replaces the old static generateCostReport()
  // export. Prints ONLY the currently active tab (Material/Labour/
  // Subcontract/Equipment/Indirect/a specific Category/Uncategorized/
  // Summary) — exactly what's on screen right now — using ONLY the
  // columns currently visible (visibleCols). No new financial logic: this
  // reuses buildCostControlPdfModel()'s existing per-tab filtering (the
  // same rule the on-screen table itself uses for activeDirectRows/
  // activeTotals) with a single-section list, and reuses the same
  // renderer/print-window pattern as exportPdf() — printed in a separate,
  // non-scrolling page (not window.print() on the live DOM) so
  // page-break-after actually works, avoiding the earlier documented
  // overflow:auto page-break bug.
  function printCurrentScreenView() {
    if (!reportData.directRows && !reportData.indirectRows) {
      alert('No data available. Please select a project and month first.')
      return
    }
    const isSummaryTab = directResourceTab === 'summary'
    const sections = isSummaryTab ? ['summary'] : [directResourceTab]

    let dataSourceLabel = 'Current (Live)'
    let releaseInfo = null
    if (reportContext.source === 'release') {
      const release = availableReleases.find(r => r.id === reportContext.releaseId)
      dataSourceLabel = 'Project Forecast Release'
      if (release) {
        releaseInfo = {
          releaseNumber: release.release_number,
          forecastPeriodLabel: new Date(release.forecast_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          directRevisionLabel: release.direct_snapshot_id ? `Rev.${release.direct_revision_number}` : 'None',
          indirectRevisionLabel: release.indirect_snapshot_id ? `Rev.${release.indirect_revision_number}` : 'None',
        }
      }
    }

    exportCostControlPdf(reportData, {
      sections,
      allSections: false,
      projectName,
      orgName: projectOrg || '',
      orgId: projectOrgId,
      projectId,
      monthLabel: MONTHS[selectedMonth - 1],
      year: selectedYear,
      generatedBy: '',
      dataSourceLabel,
      releaseInfo,
      contractValue,
      summary,
      // monthlyActualCost — the EXACT SAME object this screen's own
      // Executive Cards / Summary "This Period" column are already
      // rendering from (state loaded once in buildReport()). Passed
      // straight through so the screen and the PDF can never diverge
      // onto two independently-fetched copies of the same Movement data.
      monthlyActualCost,
      // visibleCols — the EXACT column-visibility state the on-screen
      // table is using right now (Commitment/Accrual/Var@EAC/EV/CPI). The
      // printed table shows only these optional columns, matching the
      // screen pixel-for-pixel in terms of which figures are shown.
      visibleCols,
      includeCoverPage: false,
      includeExecutiveSummary: false, // this is a single-tab print, not a full report — no separate project-wide summary page
    })
  }

  // exportPdf() — orchestration only, matching the CostControl-side
  // responsibility exportCostControlPdf() expects: gather the current
  // screen's context (which data source is active, which section is
  // selected, the current Release's composition if applicable) into
  // plain options, and delegate entirely to exportCostControlPdf(). No
  // model-building or rendering logic lives here — this function never
  // computes a financial figure, it only assembles labels/strings for
  // display and passes reportData through unchanged.
  //
  // PHASE 1 (explicitly agreed): defaults sections to the single
  // currently active tab (directResourceTab) — the model/renderer
  // already support a full sections list without any change, so
  // widening this default later (e.g. an "Export All Sections" button)
  // requires no redesign, only a different sections array here.
  function exportPdf() {
    if (!reportData.directRows && !reportData.indirectRows) {
      alert('No data available. Please select a project and month first.')
      return
    }
    const isIndirectTab = directResourceTab === 'indirect' || directResourceTab.startsWith('cat:')
    const isSummaryTab = directResourceTab === 'summary'
    const sections = isSummaryTab ? [] : [directResourceTab]

    let dataSourceLabel = 'Current (Live)'
    let releaseInfo = null
    if (reportContext.source === 'release') {
      const release = availableReleases.find(r => r.id === reportContext.releaseId)
      dataSourceLabel = 'Project Forecast Release'
      if (release) {
        releaseInfo = {
          releaseNumber: release.release_number,
          forecastPeriodLabel: new Date(release.forecast_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          directRevisionLabel: release.direct_snapshot_id ? `Rev.${release.direct_revision_number}` : 'None',
          indirectRevisionLabel: release.indirect_snapshot_id ? `Rev.${release.indirect_revision_number}` : 'None',
        }
      }
    }

    exportCostControlPdf(reportData, {
      // Phase 2: export every section the screen has (dynamic, matches
      // whatever tabs are actually showing — Material/Labour/Subcontract/
      // Equipment + real Indirect Categories + Uncategorized + Summary).
      allSections: true,
      projectName,
      orgName: projectOrg || '',
      orgId: projectOrgId,
      projectId,
      monthLabel: MONTHS[selectedMonth - 1],
      year: selectedYear,
      generatedBy: '', // no user-name source currently wired (see prior discussion on created_by/user profiles) — left blank rather than fabricated
      dataSourceLabel,
      releaseInfo,
      contractValue,
      // summary — the EXACT same object the screen's own Executive Cards
      // render from (setSummary() inside buildReport()). Passed through
      // unchanged so the PDF's Executive Summary cards and the on-screen
      // Executive Cards share one source of truth and can never diverge.
      summary,
      // monthlyActualCost — the EXACT SAME object this screen's own
      // Executive Cards / Summary "This Period" column are already
      // rendering from. Passed straight through so the screen and the
      // full PDF export can never diverge onto two independently-fetched
      // copies of the same Movement data.
      monthlyActualCost,
      // visibleCols — was previously missing from this call entirely,
      // which meant buildCostControlPdfModel() always received
      // visibleCols = null and silently hid Commitment/Accrual/EV/CPI
      // regardless of what the user had actually selected on screen.
      // Now the full "↓ PDF" export respects the exact same column
      // selection as printCurrentScreenView() already did.
      visibleCols,
      // includeCoverPage intentionally NOT hardcoded here — left for
      // exportCostControlPdf.js to resolve from the project's saved
      // cost_control_report_config (Release Management → Reports tab),
      // defaulting to true only if no config has been saved yet.
    })
  }

  const fmt = (n) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n || 0)
  const fmtK = (n) => {
    const abs = Math.abs(n || 0)
    if (abs >= 1000000) return (n/1000000).toFixed(1) + 'M'
    if (abs >= 1000) return (n/1000).toFixed(0) + 'K'
    return fmt(n)
  }
  const varColor = (n) => n >= 0 ? 'text-green-600' : 'text-red-600'
  const toggleCol = (key) => setVisibleCols(p => ({ ...p, [key]: !p[key] }))
  const varBadge = (eac, bac) => {
    const diff = eac - bac
    if (diff <= 0) return { bg: 'rgba(26,122,74,.07)', color: '#1A7A4A' }
    const pct = bac > 0 ? (diff / bac) * 100 : 100
    if (pct <= 5) return { bg: 'rgba(217,119,6,.08)', color: '#D97706' }
    return { bg: 'rgba(220,38,38,.08)', color: '#DC2626' }
  }

  const toggleFlag = (id) => setFlagged(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    try { localStorage.setItem('cc_flagged', JSON.stringify([...next])) } catch {}
    return next
  })

  const [selectedRowId, setSelectedRowId] = useState(null)

  const openDrill = (row) => { setDrillRow(row); setSelectedRowId(row.id) }

  // Measure the actual header row height so the sticky Grand Total row's offset
  // is never a stale magic number if header/column-header height changes later.
  // Placed BEFORE the early return below so this Hook always runs in the same order.
  useLayoutEffect(() => {
    if (!theadRef.current) return
    const measure = () => {
      if (theadRef.current) setTheadHeight(theadRef.current.offsetHeight)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(theadRef.current)
    return () => ro.disconnect()
  }, [directResourceTab, visibleCols])

  // ── Row cells component ──

  if (!projectId) return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-[#1C1A18] mb-6">Monthly Cost Control Report</h1>
      <p className="text-[#6B655C] mb-4">Select a project to continue</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {projects.map(p => (
          <button key={p.id} onClick={() => { setProjectId(p.id); setProjectName(p.name); setProjectOrg(p.organizations?.name || ''); setProjectOrgId(p.organization_id || null); setContractValue(p.contract_value || 0); buildReport(p.id) }}
            className="text-left bg-white border border-[#DDD9D0] rounded-xl p-4 hover:border-[#1A7A4A] hover:shadow-sm transition-all">
            <p className="font-semibold text-[#1C1A18]">{p.name}</p>
            <p className="text-sm text-[#6B655C] mt-0.5">{p.organizations?.name}</p>
          </button>
        ))}
      </div>
    </div>
  )

  // ── Row cells component ──
  function RowCells({ r }) {
    const vc = varBadge(r.eac, r.bac)
    // VAC (Variance at Completion) = BAC - EAC — a positive value means the
    // project is forecast to finish under budget. This must match
    // SummaryView's calculation exactly (vac = data.bac - data.eac): there is
    // only one business definition of VAC, consumed the same way everywhere.
    const diff = r.bac - r.eac
    const td = 'padding:8px 14px, textAlign:right, fontVariantNumeric:tabular-nums, borderRight:1px solid #F3F4F6'
    return (
      <>
        <td style={{ padding:'8px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:600, borderRight:'1px solid #E5E7EB' }}>{fmt(r.bac)}</td>
        {visibleCols.commitment && <td style={{ padding:'8px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', borderRight:'1px solid #E5E7EB' }}>{fmt(r.commitment)}</td>}
        {visibleCols.accrual && <td style={{ padding:'8px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', color:'#D97706', borderRight:'2px solid #D1D5DB' }}>{r.accrual > 0 ? fmt(r.accrual) : '—'}</td>}
        <td style={{ padding:'8px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:700, borderRight:'2px solid #D1D5DB' }}>{fmt(r.totalActualCost)}</td>
        <td style={{ padding:'8px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', color:'#6B7280', borderRight:'1px solid #E5E7EB' }}>{fmt((r.eac||0)-(r.totalActualCost||0))}</td>
        <td style={{ padding:'8px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:700, color: r.eac > r.bac ? '#DC2626' : '#1A7A4A', borderRight:'1px solid #E5E7EB' }}>{fmt(r.eac)}</td>
        {visibleCols.varEAC && <td style={{ padding:'8px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', color: r.varianceEac >= 0 ? '#1A7A4A' : '#DC2626', borderRight:'1px solid #E5E7EB' }}>{r.varianceEac !== 0 ? fmt(r.varianceEac) : '—'}</td>}
        {visibleCols.ev && <td style={{ padding:'8px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', color:'#7C3AED', borderRight:'1px solid #E5E7EB' }}>{r.earnedValue > 0 ? fmt(r.earnedValue) : '—'}</td>}
        {visibleCols.cpi && <td style={{ padding:'8px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:600, color: !r.cpi ? '#9CA3AF' : r.cpi >= 1 ? '#1A7A4A' : '#DC2626', borderRight:'1px solid #E5E7EB' }}>{r.cpi ? r.cpi.toFixed(2) : '—'}</td>}
        {visibleCols.costVariance && (() => {
          const cv = (r.earnedValue || 0) - (r.totalActualCost || 0)
          return <td style={{ padding:'8px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:600, color: cv >= 0 ? '#1A7A4A' : '#DC2626', borderRight:'1px solid #E5E7EB' }}>{(cv >= 0 ? '+' : '') + fmt(cv)}</td>
        })()}
        <td style={{ padding:'8px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', borderRight:'1px solid #E5E7EB' }}>
          <span style={{ background: vc.bg, borderRadius:4, padding:'2px 6px', color: vc.color, fontWeight:600 }}>
            {diff >= 0 ? '+' : ''}{fmt(diff)}
          </span>
        </td>
      </>
    )
  }

  const colCount = 5 // includes the new ETC column
    + (visibleCols.commitment ? 1 : 0)
    + (visibleCols.accrual ? 1 : 0)
    + (visibleCols.varEAC ? 1 : 0)
    + (visibleCols.ev ? 1 : 0)
    + (visibleCols.cpi ? 1 : 0)
    + (visibleCols.costVariance ? 1 : 0)

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'calc(100vh - 60px)', fontFamily:"-apple-system,'Helvetica Neue',Helvetica,Arial,sans-serif" }}>

      {/* ── Page Header ── */}
      <div style={{ padding:'8px 24px 6px', background:'#fff', borderBottom:'1px solid #E5E7EB', display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexShrink:0 }}>
        <div>
          <div style={{ fontSize:16.5, fontWeight:800, letterSpacing:'-.02em', color:'#0F1017' }}>Monthly Cost Control Report</div>
          <div style={{ fontSize:11, color:'#6B7280', marginTop:2 }}>
            {projectName}{orgName ? ` · ${orgName}` : ''} · {MONTHS[selectedMonth-1]} {selectedYear}
            <button onClick={() => setProjectId(null)} style={{ marginLeft:8, fontSize:10, color:'#9CA3AF', border:'1px solid #E5E7EB', borderRadius:4, padding:'1px 6px', cursor:'pointer', background:'none' }}>× Change</button>
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button onClick={() => setShowPeriodModal(true)}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', border:'1px solid #D1D5DB', borderRadius:8, fontSize:12, background:'#fff', color:'#374151', cursor:'pointer' }}>
            📅 {MONTHS[selectedMonth-1]}-{selectedYear}
          </button>
          <button onClick={() => buildReport()} disabled={loading}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:8, background:'#1A7A4A', color:'#fff', border:'none', fontSize:12, fontWeight:600, cursor:'pointer', opacity: loading?0.6:1 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width:13, height:13 }}><path d="M23 4v6h-6M1 20v-6h6" strokeLinecap="round" strokeLinejoin="round"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Refresh
          </button>
          <button onClick={exportToExcel}
            style={{ padding:'7px 14px', borderRadius:8, background:'#fff', color:'#374151', border:'1px solid #D1D5DB', fontSize:12, fontWeight:500, cursor:'pointer' }}>
            ↓ Excel
          </button>
          <button onClick={printCurrentScreenView}
            style={{ padding:'7px 14px', borderRadius:8, background:'#fff', color:'#374151', border:'1px solid #D1D5DB', fontSize:12, fontWeight:500, cursor:'pointer' }}>
            🖨️ Print Screen
          </button>
          <button onClick={exportPdf}
            style={{ padding:'7px 14px', borderRadius:8, background:'#fff', color:'#374151', border:'1px solid #D1D5DB', fontSize:12, fontWeight:500, cursor:'pointer' }}>
            ↓ PDF
          </button>
        </div>
      </div>

      {/* ── Data Source Bar — ALWAYS visible, per explicit agreement: the
          selected financial state must never be ambiguous. Default is
          Current (Live); switching to a Project Forecast Release is a
          deliberate user action. ── */}
      <div style={{ padding:'8px 24px', background: reportContext.source === 'release' ? '#FFFBEB' : '#F9FAFB',
        borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', gap:16, flexShrink:0 }}>
        <span style={{ fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#6B7280' }}>Data Source:</span>
        <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:12.5, fontWeight:600, cursor:'pointer', color: reportContext.source==='live' ? '#0F1017' : '#6B7280' }}>
          <input type="radio" checked={reportContext.source === 'live'}
            onChange={() => setReportContext({ source: 'live', releaseId: null })}/>
          Current (Live)
        </label>
        <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:12.5, fontWeight:600, cursor:'pointer', color: reportContext.source==='release' ? '#0F1017' : '#6B7280' }}>
          <input type="radio" checked={reportContext.source === 'release'}
            onChange={() => {
              // Selecting the mode with no release chosen yet defaults to
              // the most recent Release, if any exist.
              const latest = availableReleases[0]
              setReportContext({ source: 'release', releaseId: latest ? latest.id : null })
            }}/>
          Project Forecast Release
        </label>
        {reportContext.source === 'release' && (
          availableReleases.length === 0 ? (
            <span style={{ fontSize:12, color:'#9CA3AF', fontStyle:'italic' }}>No Releases exist yet for this project.</span>
          ) : (
            <select value={reportContext.releaseId || ''} onChange={e => setReportContext({ source: 'release', releaseId: e.target.value })}
              style={{ fontSize:12.5, fontWeight:600, color:'#92400E', border:'1px solid #FDE68A', background:'#FFFBEB', borderRadius:6, padding:'4px 10px' }}>
              {availableReleases.map((r, i) => {
                const directLabel = r.direct_snapshot_id ? `Direct Rev.${r.direct_revision_number}` : 'No Direct'
                const indirectLabel = r.indirect_snapshot_id ? `Indirect Rev.${r.indirect_revision_number}` : 'No Indirect'
                return (
                  <option key={r.id} value={r.id}>
                    {/* Release # is the primary identifier — period and
                        current-flag support it; revision detail trails
                        clearly separated by "·", never blended into one
                        long clause (explicitly requested). */}
                    Release #{r.release_number}{r.is_current ? ' (Current)' : ''} — {new Date(r.forecast_date).toLocaleDateString('en-US',{month:'short',year:'numeric'})} · {directLabel} · {indirectLabel}
                  </option>
                )
              })}
            </select>
          )
        )}
        {reportContext.source === 'release' && reportContext.releaseId && (
          <span style={{ fontSize:11, color:'#92400E', display:'flex', alignItems:'center', gap:4 }}>
            🔒 Read-only — approved historical state
          </span>
        )}
      </div>

      {/* ── Summary Bar — light background matching header (no 3rd tone), BAC given primary emphasis ── */}
      <div style={{ display:'flex', alignItems:'center', gap:0, padding:'8px 24px', background:'#fff', flexShrink:0, borderBottom:'1px solid #E5E7EB', overflowX:'auto' }}>
        {(() => {
          const varComp = summary.totalBudget - summary.totalEAC
          const vcBadge = varBadge(summary.totalEAC, summary.totalBudget)
          const items = [
            { label:'BAC (Budget)', value: fmtK(summary.totalBudget), sub: null, primary: true },
            { label:'Commitment', value: fmtK(summary.totalCommitment), sub: summary.totalBudget > 0 ? (summary.totalCommitment/summary.totalBudget*100).toFixed(1)+'% of BAC' : null },
            { label:'Accrual', value: fmtK(summary.totalAccrual), color:'#D97706', sub: null },
            { label:'Actual Cost', value: fmtK(summary.totalActual), color:'#DC2626', sub: summary.totalBudget > 0 ? (summary.totalActual/summary.totalBudget*100).toFixed(1)+'% of BAC' : null },
            // This Period — read directly from monthlyActualCost.grandMovement
            // (buildMonthlyActualCostData()'s own output), the exact same
            // figure Executive Report's Grand Total row already shows. No
            // calculation happens here — this is a straight lookup.
            { label:'This Period', value: monthlyActualCost ? ((monthlyActualCost.grandMovement >= 0 ? '+' : '') + fmtK(monthlyActualCost.grandMovement)) : '—', color: (monthlyActualCost?.grandMovement || 0) >= 0 ? '#1A7A4A' : '#DC2626', sub: null },
            { label:'EAC Forecast', value: fmtK(summary.totalEAC), color:'#D97706', sub: null },
            { label:'ETC', value: fmtK(summary.totalEAC - summary.totalActual), color:'#6B7280', sub: null },
            { label:'Var @ Comp', value: (varComp>=0?'+':'')+fmtK(varComp), badge: vcBadge, sub: null },
            ...(contractValue > 0 ? [
              { label:'Contract Value', value: fmtK(contractValue), color:'#7C3AED', sub: null },
              { label:'Gross Profit', value: fmtK(contractValue - summary.totalEAC), color: contractValue >= summary.totalEAC ? '#1A7A4A' : '#DC2626', sub: null },
              { label:'GP %', value: ((contractValue - summary.totalEAC) / contractValue * 100).toFixed(1) + '%', color: contractValue >= summary.totalEAC ? '#1A7A4A' : '#DC2626', sub: null },
            ] : []),
          ]
          return items.map((s, i, arr) => (
            <div key={s.label} style={{ display:'flex', alignItems:'center', gap:0 }}>
              <div style={{ display:'flex', flexDirection:'column', gap:2, padding:'0 18px' }}>
                <div style={{ fontSize:8.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:'#6B7280' }}>{s.label}</div>
                {s.badge ? (
                  <div style={{ display:'inline-flex', alignSelf:'flex-start' }}>
                    <span style={{ background: s.badge.bg, color: s.badge.color, borderRadius:6, padding:'2px 8px', fontSize: s.primary ? 17 : 13.5, fontWeight:800 }}>{s.value}</span>
                  </div>
                ) : (
                  <div style={{ fontSize: s.primary ? 17 : 13.5, fontWeight:800, color: s.color || '#0F1017' }}>{s.value}</div>
                )}
                {s.sub && <div style={{ fontSize:9.5, color:'#6B7280' }}>{s.sub}</div>}
              </div>
              {i < arr.length - 1 && <div style={{ width:1, height:32, background:'#E5E7EB' }}/>}
            </div>
          ))
        })()}
      </div>

      {/* ── Tabs Bar ── */}
      <div style={{ display:'flex', gap:0, background:'#fff', borderBottom:'1px solid #E5E7EB', flexShrink:0, padding:'0 24px' }}>
        {[['report','Cost Control Report'],['activity','Activity Report']].map(([key, label]) => (
          <button key={key} onClick={() => setActiveTab(key)}
            style={{ padding:'7px 18px', fontSize:12.5, fontWeight:600, border:'none', background:'none', cursor:'pointer',
              color: activeTab===key ? '#1A7A4A' : '#6B7280',
              borderBottom: activeTab===key ? '2px solid #1A7A4A' : '2px solid transparent',
              marginBottom:-1 }}>
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'report' && <>
      {/* ── Filter Bar ── */}
      <div style={{ display:'flex', alignItems:'center', gap:16, padding:'7px 24px', background:'#fff', borderBottom:'1px solid #E5E7EB', flexShrink:0, flexWrap:'wrap' }}>
        {/* Search */}
        <div style={{ display:'flex', alignItems:'center', gap:7, padding:'5px 10px', background:'#F3F4F6', border:'1px solid #E5E7EB', borderRadius:8 }}>
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="#9CA3AF" strokeWidth="1.4"><circle cx="6.5" cy="6.5" r="4.5"/><line x1="9.5" y1="9.5" x2="14" y2="14"/></svg>
          <input value={filterCode} onChange={e => {
            setFilterCode(e.target.value)
            if (e.target.value) {
              const allParents = [...(reportData.directRows||[]),...(reportData.indirectRows||[])].map(r=>r.id)
              setExpandedCodes(new Set(allParents))
            }
          }} placeholder="Search code or description…"
            style={{ border:'none', background:'none', fontSize:12, color:'#0F1017', outline:'none', width:180 }}/>
          {filterCode && <button onClick={() => setFilterCode('')} style={{ border:'none', background:'none', color:'#9CA3AF', cursor:'pointer', fontSize:11, padding:0 }}>✕</button>}
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {/* Over budget checkbox — same pill pattern as Flagged for consistency */}
          <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, cursor:'pointer',
            color: overBudgetOnly ? '#DC2626' : '#6B7280',
            background: overBudgetOnly ? '#FEF2F2' : 'transparent',
            border: overBudgetOnly ? '1px solid #FECACA' : '1px solid transparent',
            borderRadius:8, padding:'4px 10px', transition:'all .12s' }}>
            <input type="checkbox" checked={overBudgetOnly} onChange={e => setOverBudgetOnly(e.target.checked)} style={{ cursor:'pointer', accentColor:'#DC2626' }}/>
            Show over budget only
          </label>

          {/* Flagged only */}
          <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, cursor:'pointer',
            color: showFlaggedOnly ? '#D97706' : '#6B7280',
            background: showFlaggedOnly ? '#FFFBEB' : 'transparent',
            border: showFlaggedOnly ? '1px solid #FDE68A' : '1px solid transparent',
            borderRadius:8, padding:'4px 10px', transition:'all .12s' }}>
            <input type="checkbox" checked={showFlaggedOnly} onChange={e => setShowFlaggedOnly(e.target.checked)} style={{ cursor:'pointer', accentColor:'#D97706' }}/>
            ⚑ Flagged only {showFlaggedOnly && flagged.size > 0 && <span style={{ background:'#D97706', color:'#fff', borderRadius:10, padding:'0 5px', fontSize:10, fontWeight:700, marginLeft:2 }}>{flagged.size}</span>}
          </label>
        </div>

        <div style={{ flex:1 }}/>

        {/* Expand / Collapse / Columns — grouped together, standard button padding */}
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <button onClick={() => setExpandedCodes(new Set([...(reportData.directRows||[]),...(reportData.indirectRows||[])].map(r=>r.id)))}
            style={{ padding:'7px 14px', border:'1px solid #E5E7EB', borderRadius:8, fontSize:12, background:'transparent', color:'#6B7280', cursor:'pointer' }}>▼ Expand All</button>
          <button onClick={() => setExpandedCodes(new Set())}
            style={{ padding:'7px 14px', border:'1px solid #E5E7EB', borderRadius:8, fontSize:12, background:'transparent', color:'#6B7280', cursor:'pointer' }}>▶ Collapse</button>

          {/* Columns toggle */}
          <div style={{ position:'relative' }}>
            <button onClick={() => setColPanelOpen(v => !v)}
              style={{ padding:'7px 14px', border:'1px solid #E5E7EB', borderRadius:8, fontSize:12, background:'transparent', color:'#6B7280', cursor:'pointer' }}>
              Columns ▾
            </button>
            {colPanelOpen && (
              <div style={{ position:'absolute', top:'calc(100% + 6px)', right:0, zIndex:50, background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, padding:'10px 12px', minWidth:180, boxShadow:'0 4px 12px rgba(0,0,0,.1)' }}>
                <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:'#6B7280', marginBottom:8 }}>Visible Columns</div>
                {[
                  ['commitment','Commitment'],['accrual','Accrual'],
                  ['varEAC','Var EAC'],['ev','Earned Value'],['cpi','CPI'],['costVariance','Cost Variance'],
                ].map(([key, label]) => (
                  <div key={key} onClick={() => toggleCol(key)}
                    style={{ display:'flex', alignItems:'center', gap:8, padding:'4px 6px', cursor:'pointer', borderRadius:4 }}>
                    <div style={{ width:14, height:14, borderRadius:3, border: visibleCols[key] ? 'none' : '1.5px solid #D1D5DB', background: visibleCols[key] ? '#1A7A4A' : 'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      {visibleCols[key] && <span style={{ color:'#fff', fontSize:10, lineHeight:1 }}>✓</span>}
                    </div>
                    <span style={{ fontSize:12, color:'#374151' }}>{label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Resource-Type Tabs (Material/Labour/Subcontract/Equipment/[Indirect or Category tabs]/Summary) ── */}
      {/* Segmented-control style — deliberately distinct from the underline Primary Tabs above */}
      <div style={{ padding:'7px 24px', background:'#fff', borderBottom:'1px solid #E5E7EB', flexShrink:0 }}>
        <div style={{ display:'inline-flex', gap:2, padding:3, background:'#F3F4F6', borderRadius:10, flexWrap:'wrap' }}>
          {(() => {
            const baseTabs = [
              ['material','Material','#2563EB'],
              ['labour','Labour','#16A34A'],
              ['subcontract','Subcontract','#DC2626'],
              ['equipment','Equipment','#D97706'],
            ]
            const hasCategories = (reportData.indirectCategories || []).length > 0
            const indirectTabs = hasCategories
              ? [
                  ...reportData.indirectCategories.map(cat => [`cat:${cat.id}`, cat.name, '#7C3AED']),
                  ...((reportData.uncategorizedIndirectRows || []).length > 0 ? [['cat:uncategorized', 'Uncategorized', '#9CA3AF']] : []),
                ]
              : [['indirect','Indirect','#7C3AED']]
            const allTabs = [...baseTabs, ...indirectTabs, ['summary','Summary','#1B3A6B']]
            return allTabs.map(([key, label, color]) => (
              <button key={key} onClick={() => setDirectResourceTab(key)}
                style={{ padding:'6px 16px', fontSize:12, fontWeight:600, borderRadius:8, cursor:'pointer',
                  border: directResourceTab===key ? `1px solid ${color}33` : '1px solid transparent',
                  background: directResourceTab===key ? '#fff' : 'transparent',
                  color: directResourceTab===key ? color : '#6B7280',
                  boxShadow: directResourceTab===key ? '0 1px 4px rgba(0,0,0,.12)' : 'none',
                  transition:'all .12s' }}>
                {label}
              </button>
            ))
          })()}
        </div>
      </div>

      {/* ── Table ── */}
      {(() => {
        // Filter Direct Cost Codes down to only those with a matching Resource Code for
        // the active tab (Material/Labour/Subcontract/Equipment). For 'indirect' or a category
        // tab ('cat:xxx'), use the matching Indirect rows. For 'summary', not used here.
        const isIndirectTab = directResourceTab === 'indirect' || directResourceTab.startsWith('cat:')
        const isSummaryTab = directResourceTab === 'summary'
        const activeDirectRows = isSummaryTab || isIndirectTab ? [] : (reportData.directRows || [])
          .filter(parent => (parent.resourceRows || []).some(r => r.resource_type === directResourceTab))

        // Resolve which Indirect rows/totals belong to the active tab
        let activeIndirectRows = []
        let indirectTabLabel = 'Indirect Works'
        if (directResourceTab === 'indirect') {
          activeIndirectRows = reportData.indirectRows || []
        } else if (directResourceTab === 'cat:uncategorized') {
          activeIndirectRows = reportData.uncategorizedIndirectRows || []
          indirectTabLabel = 'Uncategorized'
        } else if (directResourceTab.startsWith('cat:')) {
          const catId = directResourceTab.slice(4)
          const cat = (reportData.indirectCategories || []).find(c => c.id === catId)
          activeIndirectRows = cat?.rows || []
          indirectTabLabel = cat?.name || 'Category'
        }

        // Totals must reflect ONLY what's shown in this tab — summed from the Resource Code
        // rows matching the tab (not the whole Cost Code, which may include other resource types)
        const sumFields = ['bac','commitment','actualTotal','totalActualCost','eac','prevEac','accrual']
        function emptyTotals() { const t = {}; sumFields.forEach(k => t[k]=0); return t }

        let activeTotals = emptyTotals()
        if (directResourceTab === 'indirect') {
          activeTotals = reportData.indirectTotals || emptyTotals()
        } else if (directResourceTab === 'cat:uncategorized') {
          activeTotals = reportData.uncategorizedTotals || emptyTotals()
        } else if (directResourceTab.startsWith('cat:')) {
          const catId = directResourceTab.slice(4)
          const cat = (reportData.indirectCategories || []).find(c => c.id === catId)
          activeTotals = cat?.totals || emptyTotals()
        } else if (!isSummaryTab) {
          for (const parent of activeDirectRows) {
            const matchingResources = (parent.resourceRows || []).filter(r => r.resource_type === directResourceTab)
            for (const res of matchingResources) {
              for (const k of sumFields) activeTotals[k] = (activeTotals[k]||0) + (res[k]||0)
            }
          }
          activeTotals.varianceEac = activeTotals.eac - activeTotals.prevEac
          activeTotals.varianceCompletion = activeTotals.bac - activeTotals.eac
        }

        return loading ? (
        <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:12 }}>
          <div style={{ width:28, height:28, border:'3px solid #E5E7EB', borderTopColor:'#1A7A4A', borderRadius:'50%', animation:'spin 1s linear infinite' }}/>
          <p style={{ fontSize:13, color:'#6B7280' }}>Building report...</p>
        </div>
      ) : (isIndirectTab && reportData.hasIndirectSnapshot === false) ? (
        // Explicit state — distinct from an ordinary empty report
        // (SS17.2's agreed validation: a Release may legitimately have
        // only a Direct side). Zero rows because "no snapshot attached"
        // and zero rows because "this Revision genuinely has nothing here"
        // must never look the same to the user (explicitly requested).
        <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:8, padding:40 }}>
          <div style={{ fontSize:32 }}>—</div>
          <p style={{ fontSize:13.5, fontWeight:600, color:'#374151' }}>No Indirect Revision is attached to this Release.</p>
          <p style={{ fontSize:12, color:'#9CA3AF', maxWidth:420, textAlign:'center' }}>
            This Project Forecast Release includes only a Direct Revision. It does not represent zero Indirect cost — Indirect simply was not part of this approval.
          </p>
        </div>
      ) : (!isIndirectTab && !isSummaryTab && reportData.hasDirectSnapshot === false) ? (
        <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:8, padding:40 }}>
          <div style={{ fontSize:32 }}>—</div>
          <p style={{ fontSize:13.5, fontWeight:600, color:'#374151' }}>No Direct Revision is attached to this Release.</p>
          <p style={{ fontSize:12, color:'#9CA3AF', maxWidth:420, textAlign:'center' }}>
            This Project Forecast Release includes only an Indirect Revision. It does not represent zero Direct cost — Direct simply was not part of this approval.
          </p>
        </div>
      ) : isSummaryTab ? (
        <SummaryView reportData={reportData} contractValue={contractValue} fmt={fmt} monthlyActualCost={monthlyActualCost} visibleCols={visibleCols} />
      ) : (
        <div style={{ flex:1, overflow:'auto', margin:'10px 24px 16px' }}>
          <div style={{ border:'1px solid #E5E7EB', borderRadius:12, background:'#fff' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
              <thead ref={theadRef} style={{ position:'sticky', top:0, zIndex:20 }}>
                <tr>
                  <th style={{ padding:'9px 14px', textAlign:'left', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', background:'#F9FAFB', borderBottom:'1px solid #E5E7EB', position:'sticky', left:0, zIndex:11, minWidth:110, whiteSpace:'nowrap', borderRight:'1px solid #E5E7EB' }}>Cost Code</th>
                  <th style={{ padding:'9px 14px', textAlign:'left', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', background:'#F9FAFB', borderBottom:'1px solid #E5E7EB', borderRight:'2px solid #D1D5DB', position:'sticky', left:110, zIndex:11, minWidth:200, whiteSpace:'nowrap' }}>Description</th>
                  <th style={{ padding:'9px 14px', textAlign:'right', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', background:'#F9FAFB', borderBottom:'1px solid #E5E7EB', minWidth:90, whiteSpace:'nowrap', borderRight:'1px solid #E5E7EB' }}>BAC</th>
                  {visibleCols.commitment && <th style={{ padding:'9px 14px', textAlign:'right', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', background:'#F9FAFB', borderBottom:'1px solid #E5E7EB', minWidth:90, whiteSpace:'nowrap', borderRight:'1px solid #E5E7EB' }}>Commitment</th>}
                  {visibleCols.accrual && <th title="مصاريف مستحقة لسه متسجلتش رسمي — نفس لون EAC لأن الاتنين أرقام تقديرية" style={{ padding:'9px 14px', textAlign:'right', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#D97706', background:'#F9FAFB', borderBottom:'1px solid #E5E7EB', minWidth:80, whiteSpace:'nowrap', borderRight:'1px solid #E5E7EB', cursor:'help' }}>Accrual</th>}
                  <th style={{ padding:'9px 14px', textAlign:'right', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', background:'#F9FAFB', borderBottom:'1px solid #E5E7EB', minWidth:90, whiteSpace:'nowrap', borderRight:'1px solid #E5E7EB' }}>
                    Actual Cost<br/><span style={{ fontWeight:500, fontSize:9, textTransform:'none', letterSpacing:0 }}>({MONTHS[selectedMonth-1]}-{selectedYear})</span>
                  </th>
                  <th style={{ padding:'9px 14px', textAlign:'right', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', background:'#F9FAFB', borderBottom:'1px solid #E5E7EB', minWidth:90, whiteSpace:'nowrap', borderRight:'1px solid #E5E7EB' }}>ETC</th>
                  <th style={{ padding:'9px 14px', textAlign:'right', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', background:'#F9FAFB', borderBottom:'1px solid #E5E7EB', minWidth:90, whiteSpace:'nowrap', borderRight:'1px solid #E5E7EB' }}>EAC</th>
                  {visibleCols.varEAC && <th style={{ padding:'9px 14px', textAlign:'right', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', background:'#F9FAFB', borderBottom:'1px solid #E5E7EB', minWidth:70, whiteSpace:'nowrap', borderRight:'1px solid #E5E7EB' }}>Var @ EAC</th>}
                  {visibleCols.ev && <th style={{ padding:'9px 14px', textAlign:'right', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#7C3AED', background:'#F9FAFB', borderBottom:'1px solid #E5E7EB', minWidth:90, whiteSpace:'nowrap', borderRight:'1px solid #E5E7EB' }}>Earned Value</th>}
                  {visibleCols.cpi && <th title="CPI = Earned Value ÷ Total Actual Cost" style={{ padding:'9px 14px', textAlign:'right', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', background:'#F9FAFB', borderBottom:'1px solid #E5E7EB', minWidth:60, whiteSpace:'nowrap', borderRight:'1px solid #E5E7EB', cursor:'help' }}>CPI</th>}
                  {visibleCols.costVariance && <th title="Cost Variance = Earned Value − Actual Cost" style={{ padding:'9px 14px', textAlign:'right', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', background:'#F9FAFB', borderBottom:'1px solid #E5E7EB', minWidth:100, whiteSpace:'nowrap', borderRight:'1px solid #E5E7EB', cursor:'help' }}>Cost Variance</th>}
                  <th style={{ padding:'9px 14px', textAlign:'right', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', background:'#F9FAFB', borderBottom:'1px solid #E5E7EB', minWidth:100, whiteSpace:'nowrap', borderRight:'1px solid #E5E7EB' }}>Var @ Comp</th>
                  <th style={{ padding:'9px 14px', textAlign:'left', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', background:'#F9FAFB', borderBottom:'1px solid #E5E7EB', minWidth:140, whiteSpace:'nowrap', borderRight:'1px solid #E5E7EB' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Grand Total Row — now reflects ONLY the active tab's totals, not the whole project */}
                {!filterCode && (
                  <tr style={{ background:'#E5E9EF', color:'#0F1017', position:'sticky', top:theadHeight, zIndex:15, borderTop:'1px solid #B0B8C4', borderBottom:'1px solid #B0B8C4' }}>
                    <td style={{ padding:'9px 14px', fontSize:12, letterSpacing:'.04em', fontWeight:700, position:'sticky', left:0, background:'#E5E9EF', whiteSpace:'nowrap', minWidth:110, borderRight:'1px solid #D1D5DB' }}>GRAND TOTAL</td>
                    <td style={{ padding:'9px 14px', fontSize:11, position:'sticky', left:110, background:'#E5E9EF', minWidth:200, borderRight:'2px solid #D1D5DB', color:'#6B7280', fontWeight:400 }}>
                      {isIndirectTab ? indirectTabLabel : `${directResourceTab.charAt(0).toUpperCase()}${directResourceTab.slice(1)} — All Cost Codes`}
                    </td>
                    <RowCells r={activeTotals} />
                    <td style={{ background:'#E5E9EF' }}/>
                  </tr>
                )}

                {!isIndirectTab && (activeDirectRows || [])
                  .filter(parent => {
                    if (overBudgetOnly && parent.eac <= parent.bac) return false
                    if (showFlaggedOnly) {
                      const childFlagged = (parent.children||[]).some(c => flagged.has(c.id))
                      if (!childFlagged && !flagged.has(parent.id)) return false
                    }
                    if (!filterCode) return true
                    const q = filterCode.toLowerCase()
                    return parent.code.toLowerCase().includes(q) ||
                      parent.description?.toLowerCase().includes(q) ||
                      (parent.children||[]).some(c =>
                        c.code.toLowerCase().includes(q) ||
                        c.description?.toLowerCase().includes(q)
                      )
                  })
                  .map(parent => {
                    const isExpanded = expandedCodes.has(parent.id)
                    const filteredChildren = (() => {
                      let ch = filterCode
                        ? (parent.children||[]).filter(c =>
                            c.code.toLowerCase().includes(filterCode.toLowerCase()) ||
                            c.description?.toLowerCase().includes(filterCode.toLowerCase()) ||
                            parent.code.toLowerCase().includes(filterCode.toLowerCase())
                          )
                        : (parent.children||[])
                      if (showFlaggedOnly) ch = ch.filter(c => flagged.has(c.id))
                      // Resource-type tab filter (Direct Works only): keep only Sub Codes under a
                      // Resource Code matching the active tab. Legacy Sub Codes with no Resource Code
                      // parent are hidden once a specific type tab is active (can't tell their type).
                      if (directResourceTab !== 'summary') {
                        ch = ch.filter(c => {
                          if (!c.isUnderResource) return false
                          const resRow = (parent.resourceRows || []).find(r => r.id === c.parent_id)
                          return resRow && resRow.resource_type === directResourceTab
                        })
                      }
                      return ch
                    })()
                    const showChildren = isExpanded || !!filterCode || showFlaggedOnly

                    // The Cost Code row's totals must reflect ONLY the Resource Codes visible
                    // in the active tab (e.g. Material), not the whole Cost Code's original total
                    // which may include Subcontract/Equipment/etc rows not shown in this tab.
                    const displayParent = (() => {
                      if (directResourceTab === 'summary') return parent
                      const matchingResources = (parent.resourceRows || []).filter(r => r.resource_type === directResourceTab)
                      const sumFields = ['bac','commitment','actualTotal','actualThisPeriod','actualPrevious','accrual','totalActualCost','cashOut','cashOutThisPeriod','eac','prevEac']
                      const agg = {}
                      sumFields.forEach(k => { agg[k] = matchingResources.reduce((s,r) => s + (r[k]||0), 0) })
                      agg.varianceEac = agg.eac - agg.prevEac
                      agg.varianceCompletion = agg.bac - agg.eac
                      return { ...parent, ...agg }
                    })()
                    return (
                      <>
                        <tr key={parent.id} onClick={() => toggleExpand(parent.id)}
                          style={{ cursor:'pointer', outline: selectedRowId===parent.id ? '1px solid #0F1017' : 'none', outlineOffset:-1, borderBottom: !isExpanded ? '1px solid #D1D5DB' : 'none' }}
                          onMouseEnter={e => { Array.from(e.currentTarget.cells).forEach(td => td.style.background='#F1F3F5') }}
                          onMouseLeave={e => { Array.from(e.currentTarget.cells).forEach(td => td.style.background='#EEF0F3') }}>
                          <td style={{ padding:'12px 14px', position:'sticky', left:0, background:'#EEF0F3', borderTop:'1px solid #D1D5DB', fontWeight:700, borderRight:'1px solid #E5E7EB' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                              <span style={{ fontSize:12, color:'#1A7A4A', fontWeight:700 }}>{isExpanded ? '▼' : '▶'}</span>
                              <span style={{ fontFamily:'monospace', fontSize:12 }}>{parent.code}</span>
                            </div>
                          </td>
                          <td style={{ padding:'12px 14px', position:'sticky', left:110, background:'#EEF0F3', fontWeight:700, color:'#0F1017', fontSize:12.5, borderTop:'1px solid #D1D5DB', borderRight:'2px solid #D1D5DB' }}>{parent.description}</td>
                          <RowCells r={displayParent} />
                          <td style={{ padding:'12px 14px', background:'#EEF0F3', borderTop:'1px solid #D1D5DB' }}>
                            <div style={{ display:'flex', gap:4 }}>
                              <button onClick={() => openDrill(parent)} style={{ padding:'3px 8px', fontSize:10.5, fontWeight:600, border:'1px solid #E5E7EB', background:'#fff', color:'#6B7280', borderRadius:4, cursor:'pointer' }}>Drill ↗</button>
                            </div>
                          </td>
                        </tr>

                        {showChildren && (() => {
                          // Group filteredChildren by their Resource Code parent (if any).
                          // Sub Codes not yet linked to a Resource Code render exactly as before (flat, no grouping row).
                          const grouped = []
                          const seenResourceIds = new Set()
                          for (const child of filteredChildren) {
                            if (child.isUnderResource) {
                              const resRow = (parent.resourceRows || []).find(r => r.id === child.parent_id)
                              if (resRow && !seenResourceIds.has(resRow.id)) {
                                seenResourceIds.add(resRow.id)
                                grouped.push({ type: 'resource', row: resRow })
                              }
                              grouped.push({ type: 'sub', row: child, nested: true })
                            } else {
                              grouped.push({ type: 'sub', row: child, nested: false })
                            }
                          }
                          let subIdx = 0
                          return grouped.map((g, gi) => {
                            if (g.type === 'resource') {
                              return (
                                <tr key={'res-'+g.row.id} style={{ background:'rgba(124,58,237,.1)', borderTop:'1px solid rgba(124,58,237,.2)', borderBottom:'1px solid rgba(124,58,237,.2)', cursor:'default' }}>
                                  <td style={{ padding:'5px 14px', position:'sticky', left:0, background:'rgba(124,58,237,.1)', paddingLeft:22, borderRight:'1px solid rgba(124,58,237,.2)' }}>
                                    <span style={{ display:'inline-block', fontSize:9, fontWeight:700, letterSpacing:'.04em', color:'#7C3AED', background:'rgba(124,58,237,.12)', borderRadius:4, padding:'1px 5px', marginRight:6 }}>RES</span>
                                    <span style={{ fontFamily:'monospace', fontSize:11.5, fontWeight:700, color:'#7C3AED' }}>{g.row.code}</span>
                                  </td>
                                  <td style={{ padding:'5px 14px', position:'sticky', left:110, background:'rgba(124,58,237,.1)', color:'#7C3AED', fontSize:12, fontWeight:600, borderRight:'2px solid #D1D5DB' }}>
                                    {g.row.description} <span style={{ fontWeight:400, color:'#6B7280', fontSize:10.5 }}>({g.row.subCount} sub{g.row.subCount!==1?'s':''})</span>
                                  </td>
                                  <RowCells r={g.row} />
                                  <td style={{ background:'rgba(124,58,237,.1)' }}/>
                                </tr>
                              )
                            }
                            const child = g.row
                            const idx = subIdx++
                            return (
                              <tr key={child.id}
                                style={{ background: flagged.has(child.id) ? '#FFFDF5' : (idx%2===0 ? '#fff' : '#F9FAFB'), borderBottom:'1px solid #E5E7EB', borderLeft: flagged.has(child.id) ? '2px solid #D97706' : '1px solid #F0F0F5', cursor:'default' }}
                                onMouseEnter={e => { if (!flagged.has(child.id)) Array.from(e.currentTarget.cells).forEach(td => td.style.background='#FAFAFA') }}
                                onMouseLeave={e => { if (!flagged.has(child.id)) Array.from(e.currentTarget.cells).forEach(td => td.style.background=(idx%2===0?'#fff':'#F9FAFB')) }}>
                                <td style={{ padding:'8px 14px', position:'sticky', left:0, background: flagged.has(child.id) ? '#FFFDF5' : (idx%2===0 ? '#fff' : '#F9FAFB'), paddingLeft: g.nested ? 40 : 28, borderRight:'1px solid #E5E7EB' }}>
                                  <span style={{ fontFamily:'monospace', fontSize:11, color:'#6B7280' }}>{g.nested && '↳ '}{child.code}</span>
                                </td>
                                <td style={{ padding:'8px 14px', position:'sticky', left:110, background: flagged.has(child.id) ? '#FFFDF5' : (idx%2===0 ? '#fff' : '#F9FAFB'), color:'#374151', fontSize:12.5, borderRight:'2px solid #D1D5DB' }}>{child.description}</td>
                                <RowCells r={child} />
                                <td style={{ padding:'8px 14px', background: flagged.has(child.id) ? '#FFFDF5' : (idx%2===0 ? '#fff' : '#F9FAFB') }}>
                                  <div style={{ display:'flex', gap:4 }}>
                                    <button onClick={() => toggleFlag(child.id)} style={{ padding:'3px 8px', fontSize:10.5, fontWeight:600, border:flagged.has(child.id)?'1px solid #FDE68A':'1px solid #E5E7EB', background:flagged.has(child.id)?'#FFFBEB':'#fff', color:'#D97706', borderRadius:4, cursor:'pointer' }}>⚑ {flagged.has(child.id)?'Flagged':'Flag'}</button>
                                  </div>
                                </td>
                              </tr>
                            )
                          })
                        })()}

                        {showChildren && filteredChildren.length > 0 && (
                          <tr style={{ background:'#E8E2D6', borderTop:'1px solid #C5BFB5', borderBottom:'1px solid #C5BFB5' }}>
                            <td style={{ padding:'6px 14px 6px 28px', fontSize:11, fontWeight:700, color:'#4B4540', position:'sticky', left:0, background:'#E8E2D6', minWidth:110, borderRight:'1px solid #C5BFB5' }}>↳ Subtotal</td>
                            <td style={{ padding:'6px 14px', fontSize:11, fontWeight:700, color:'#4B4540', position:'sticky', left:110, background:'#E8E2D6', minWidth:200, borderRight:'2px solid #C5BFB5' }}>{parent.description}</td>
                            <RowCells r={displayParent} />
                            <td style={{ background:'#E8E2D6' }}/>
                          </tr>
                        )}
                      </>
                    )
                  })
                }

                {isIndirectTab && (activeIndirectRows || [])
                  .filter(parent => {
                    if (overBudgetOnly && parent.eac <= parent.bac) return false
                    if (showFlaggedOnly) {
                      const childFlagged = (parent.children||[]).some(c => flagged.has(c.id))
                      if (!childFlagged && !flagged.has(parent.id)) return false
                    }
                    if (!filterCode) return true
                    const q = filterCode.toLowerCase()
                    return parent.code.toLowerCase().includes(q) ||
                      parent.description?.toLowerCase().includes(q) ||
                      (parent.children||[]).some(c =>
                        c.code.toLowerCase().includes(q) ||
                        c.description?.toLowerCase().includes(q)
                      )
                  })
                  .map(parent => {
                    const isExpanded = expandedCodes.has(parent.id)
                    const filteredChildren = (() => {
                      let ch = filterCode
                        ? (parent.children||[]).filter(c =>
                            c.code.toLowerCase().includes(filterCode.toLowerCase()) ||
                            c.description?.toLowerCase().includes(filterCode.toLowerCase()) ||
                            parent.code.toLowerCase().includes(filterCode.toLowerCase())
                          )
                        : (parent.children||[])
                      if (showFlaggedOnly) ch = ch.filter(c => flagged.has(c.id))
                      return ch
                    })()
                    const showChildren = isExpanded || !!filterCode || showFlaggedOnly
                    return (
                      <>
                        <tr key={parent.id} onClick={() => toggleExpand(parent.id)}
                          style={{ cursor:'pointer', outline: selectedRowId===parent.id ? '1px solid #0F1017' : 'none', outlineOffset:-1, borderBottom: !isExpanded ? '1px solid #D1D5DB' : 'none' }}
                          onMouseEnter={e => { Array.from(e.currentTarget.cells).forEach(td => td.style.background='#F1F3F5') }}
                          onMouseLeave={e => { Array.from(e.currentTarget.cells).forEach(td => td.style.background='#EEF0F3') }}>
                          <td style={{ padding:'12px 14px', position:'sticky', left:0, background:'#EEF0F3', borderTop:'1px solid #D1D5DB', fontWeight:700, borderRight:'1px solid #E5E7EB' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                              <span style={{ fontSize:12, color:'#6B7280', fontWeight:700 }}>{isExpanded ? '▼' : '▶'}</span>
                              <span style={{ fontFamily:'monospace', fontSize:12 }}>{parent.code}</span>
                            </div>
                          </td>
                          <td style={{ padding:'12px 14px', position:'sticky', left:110, background:'#EEF0F3', fontWeight:700, color:'#0F1017', fontSize:12.5, borderTop:'1px solid #D1D5DB', borderRight:'2px solid #D1D5DB' }}>{parent.description}</td>
                          <RowCells r={parent} />
                          <td style={{ padding:'12px 14px', background:'#EEF0F3', borderTop:'1px solid #D1D5DB' }}>
                            <div style={{ display:'flex', gap:4 }}>
                              <button onClick={() => openDrill(parent)} style={{ padding:'3px 8px', fontSize:10.5, fontWeight:600, border:'1px solid #E5E7EB', background:'#fff', color:'#6B7280', borderRadius:4, cursor:'pointer' }}>Drill ↗</button>
                            </div>
                          </td>
                        </tr>

                        {showChildren && (() => {
                          // Group filteredChildren by their Resource Code parent (if any).
                          // Sub Codes not yet linked to a Resource Code render exactly as before (flat, no grouping row).
                          const grouped = []
                          const seenResourceIds = new Set()
                          for (const child of filteredChildren) {
                            if (child.isUnderResource) {
                              const resRow = (parent.resourceRows || []).find(r => r.id === child.parent_id)
                              if (resRow && !seenResourceIds.has(resRow.id)) {
                                seenResourceIds.add(resRow.id)
                                grouped.push({ type: 'resource', row: resRow })
                              }
                              grouped.push({ type: 'sub', row: child, nested: true })
                            } else {
                              grouped.push({ type: 'sub', row: child, nested: false })
                            }
                          }
                          let subIdx = 0
                          return grouped.map((g, gi) => {
                            if (g.type === 'resource') {
                              return (
                                <tr key={'res-'+g.row.id} style={{ background:'rgba(124,58,237,.1)', borderTop:'1px solid rgba(124,58,237,.2)', borderBottom:'1px solid rgba(124,58,237,.2)', cursor:'default' }}>
                                  <td style={{ padding:'5px 14px', position:'sticky', left:0, background:'rgba(124,58,237,.1)', paddingLeft:22, borderRight:'1px solid rgba(124,58,237,.2)' }}>
                                    <span style={{ display:'inline-block', fontSize:9, fontWeight:700, letterSpacing:'.04em', color:'#7C3AED', background:'rgba(124,58,237,.12)', borderRadius:4, padding:'1px 5px', marginRight:6 }}>RES</span>
                                    <span style={{ fontFamily:'monospace', fontSize:11.5, fontWeight:700, color:'#7C3AED' }}>{g.row.code}</span>
                                  </td>
                                  <td style={{ padding:'5px 14px', position:'sticky', left:110, background:'rgba(124,58,237,.1)', color:'#7C3AED', fontSize:12, fontWeight:600, borderRight:'2px solid #D1D5DB' }}>
                                    {g.row.description} <span style={{ fontWeight:400, color:'#6B7280', fontSize:10.5 }}>({g.row.subCount} sub{g.row.subCount!==1?'s':''})</span>
                                  </td>
                                  <RowCells r={g.row} />
                                  <td style={{ background:'rgba(124,58,237,.1)' }}/>
                                </tr>
                              )
                            }
                            const child = g.row
                            const idx = subIdx++
                            return (
                              <tr key={child.id}
                                style={{ background: flagged.has(child.id) ? '#FFFDF5' : (idx%2===0 ? '#fff' : '#F9FAFB'), borderBottom:'1px solid #E5E7EB', borderLeft: flagged.has(child.id) ? '2px solid #D97706' : '1px solid #F0F0F5', cursor:'default' }}
                                onMouseEnter={e => { if (!flagged.has(child.id)) Array.from(e.currentTarget.cells).forEach(td => td.style.background='#FAFAFA') }}
                                onMouseLeave={e => { if (!flagged.has(child.id)) Array.from(e.currentTarget.cells).forEach(td => td.style.background=(idx%2===0?'#fff':'#F9FAFB')) }}>
                                <td style={{ padding:'8px 14px', position:'sticky', left:0, background: flagged.has(child.id) ? '#FFFDF5' : (idx%2===0 ? '#fff' : '#F9FAFB'), paddingLeft: g.nested ? 40 : 28, borderRight:'1px solid #E5E7EB' }}>
                                  <span style={{ fontFamily:'monospace', fontSize:11, color:'#6B7280' }}>{g.nested && '↳ '}{child.code}</span>
                                </td>
                                <td style={{ padding:'8px 14px', position:'sticky', left:110, background: flagged.has(child.id) ? '#FFFDF5' : (idx%2===0 ? '#fff' : '#F9FAFB'), color:'#374151', fontSize:12.5, borderRight:'2px solid #D1D5DB' }}>{child.description}</td>
                                <RowCells r={child} />
                                <td style={{ padding:'8px 14px', background: flagged.has(child.id) ? '#FFFDF5' : (idx%2===0 ? '#fff' : '#F9FAFB') }}>
                                  <div style={{ display:'flex', gap:4 }}>
                                    <button onClick={() => toggleFlag(child.id)} style={{ padding:'3px 8px', fontSize:10.5, fontWeight:600, border:flagged.has(child.id)?'1px solid #FDE68A':'1px solid #E5E7EB', background:flagged.has(child.id)?'#FFFBEB':'#fff', color:'#D97706', borderRadius:4, cursor:'pointer' }}>⚑ {flagged.has(child.id)?'Flagged':'Flag'}</button>
                                  </div>
                                </td>
                              </tr>
                            )
                          })
                        })()}

                        {showChildren && filteredChildren.length > 0 && (
                          <tr style={{ background:'#E8E2D6', borderTop:'1px solid #C5BFB5', borderBottom:'1px solid #C5BFB5' }}>
                            <td style={{ padding:'6px 14px 6px 28px', fontSize:11, fontWeight:700, color:'#4B4540', position:'sticky', left:0, background:'#E8E2D6', minWidth:110, borderRight:'1px solid #C5BFB5' }}>↳ Subtotal</td>
                            <td style={{ padding:'6px 14px', fontSize:11, fontWeight:700, color:'#4B4540', position:'sticky', left:110, background:'#E8E2D6', minWidth:200, borderRight:'2px solid #C5BFB5' }}>{parent.description}</td>
                            <RowCells r={parent} />
                            <td style={{ background:'#E8E2D6' }}/>
                          </tr>
                        )}
                      </>
                    )
                  })
                }
              </tbody>
            </table>
          </div>
        </div>
      )
      })()}

      </>}

      {/* ── Activity Report Tab ── */}
      {activeTab === 'activity' && (() => {
        // Two separate tables — never mixed — matching the two-mode
        // design (explicitly agreed): a Cost Code Activity answers "how
        // is this package performing," a Resource Activity answers "how
        // is this resource performing across the project." Listing them
        // in the same table conflated two different analysis dimensions.
        const filteredActivities = activities.filter(a => (a.analysisMode || 'cost_code') === activityAnalysisTab)
        return (
        <div style={{ flex:1, overflow:'auto', padding:'20px 24px', display:'flex', flexDirection:'column', gap:16 }}>
          {/* Toolbar */}
          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <div style={{ fontSize:14, fontWeight:700, color:'#0F1017' }}>Activity Report</div>
            {/* Cost Code / Resource sub-tabs */}
            <div style={{ display:'flex', gap:4, background:'#F3F4F6', padding:3, borderRadius:8 }}>
              {[['cost_code','By Cost Code'],['resource','By Resource']].map(([mode,label]) => (
                <button key={mode} onClick={() => setActivityAnalysisTab(mode)}
                  style={{ padding:'6px 12px', borderRadius:6, fontSize:12, fontWeight:600, border:'none', cursor:'pointer',
                    background: activityAnalysisTab === mode ? '#fff' : 'transparent',
                    color: activityAnalysisTab === mode ? '#1A7A4A' : '#6B7280',
                    boxShadow: activityAnalysisTab === mode ? '0 1px 2px rgba(0,0,0,.08)' : 'none' }}>
                  {label}
                </button>
              ))}
            </div>
            <div style={{ flex:1 }}/>
            {/* Expand / Collapse */}
            <button onClick={() => setExpandedActivities(new Set(filteredActivities.map(a=>a.id)))}
              style={{ padding:'7px 14px', border:'1px solid #E5E7EB', borderRadius:8, fontSize:12, background:'transparent', color:'#6B7280', cursor:'pointer' }}>▼ Expand All</button>
            <button onClick={() => setExpandedActivities(new Set())}
              style={{ padding:'7px 14px', border:'1px solid #E5E7EB', borderRadius:8, fontSize:12, background:'transparent', color:'#6B7280', cursor:'pointer' }}>▶ Collapse</button>
            {/* Columns picker */}
            <div style={{ position:'relative' }}>
              <button onClick={() => setActColsOpen(v=>!v)}
                style={{ padding:'7px 14px', border:'1px solid #D1D5DB', borderRadius:8, fontSize:12, background:'#fff', cursor:'pointer' }}>Columns ▾</button>
              {actColsOpen && (
                <div style={{ position:'absolute', top:'calc(100% + 6px)', right:0, zIndex:50, background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, padding:'10px 12px', minWidth:160, boxShadow:'0 4px 12px rgba(0,0,0,.1)' }}>
                  {[['bac','Budget (BAC)'],['commitment','Commitment'],['actual','Actual Cost'],['accrual','Accrual'],['cashOut','Cash Out'],['eac','EAC'],['varComp','Var @ Comp'],['thisPeriod','This Period']].map(([k,lbl]) => (
                    <div key={k} onClick={() => setActVisibleCols(p => ({...p,[k]:!p[k]}))} style={{ display:'flex', alignItems:'center', gap:8, padding:'4px 6px', cursor:'pointer', borderRadius:4 }}>
                      <div style={{ width:14, height:14, borderRadius:3, border: actVisibleCols[k] ? 'none' : '1.5px solid #D1D5DB', background: actVisibleCols[k] ? '#1A7A4A' : 'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        {actVisibleCols[k] && <span style={{ color:'#fff', fontSize:10, lineHeight:1 }}>✓</span>}
                      </div>
                      <span style={{ fontSize:12, color:'#374151' }}>{lbl}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {can('CostControl.Edit') && (
              <button onClick={() => { setActForm({ id: null, name: '', costCodeIds: [], analysisMode: activityAnalysisTab }); setActEditOpen(true) }}
                style={{ padding:'7px 14px', borderRadius:8, background:'#1A7A4A', color:'#fff', border:'none', fontSize:12, fontWeight:600, cursor:'pointer' }}>+ New Activity</button>
            )}
          </div>

          {/* Activities table */}
          {filteredActivities.length === 0 ? (
            <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:48, textAlign:'center', color:'#9CA3AF', fontSize:13 }}>
              {activityAnalysisTab === 'resource'
                ? 'No Resource activities defined. Click "+ New Activity" to create one.'
                : 'No Cost Code activities defined. Click "+ New Activity" to create one.'}
            </div>
          ) : (() => {
            // compute grand totals across activities in THIS tab only
            const allRows = filteredActivities.map(act => computeActivityRow(act.costCodeIds))
            const grandTotal = {
              bac: allRows.reduce((s,r)=>s+r.bac,0),
              commitment: allRows.reduce((s,r)=>s+r.commitment,0),
              actual: allRows.reduce((s,r)=>s+r.actual,0),
              accrual: allRows.reduce((s,r)=>s+r.accrual,0),
              cashOut: allRows.reduce((s,r)=>s+r.cashOut,0),
              eac: allRows.reduce((s,r)=>s+r.eac,0),
              thisPeriod: allRows.reduce((s,r)=>s+r.thisPeriod,0),
              varComp: allRows.reduce((s,r)=>s+r.varComp,0),
            }
            const gtVc = varBadge(grandTotal.eac, grandTotal.bac)
            return (
            <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, overflow:'hidden' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
                <thead>
                  <tr style={{ background:'#F9FAFB' }}>
                    <th style={{ padding:'9px 14px', textAlign:'left', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', borderBottom:'1px solid #E5E7EB', minWidth:160 }}>Activity</th>
                    <th style={{ padding:'9px 14px', textAlign:'left', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', borderBottom:'1px solid #E5E7EB', minWidth:160 }}>Cost Codes</th>
                    {actVisibleCols.bac && <th style={{ padding:'9px 14px', textAlign:'right', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', borderBottom:'1px solid #E5E7EB', minWidth:90 }}>BAC</th>}
                    {actVisibleCols.commitment && <th style={{ padding:'9px 14px', textAlign:'right', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', borderBottom:'1px solid #E5E7EB', minWidth:90 }}>Commitment</th>}
                    {actVisibleCols.actual && <th style={{ padding:'9px 14px', textAlign:'right', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', borderBottom:'1px solid #E5E7EB', minWidth:90 }}>Actual</th>}
                    {actVisibleCols.accrual && <th style={{ padding:'9px 14px', textAlign:'right', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#D97706', borderBottom:'1px solid #E5E7EB', minWidth:80 }}>Accrual</th>}
                    {actVisibleCols.cashOut && <th style={{ padding:'9px 14px', textAlign:'right', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', borderBottom:'1px solid #E5E7EB', minWidth:80 }}>Cash Out</th>}
                    {actVisibleCols.eac && <th style={{ padding:'9px 14px', textAlign:'right', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', borderBottom:'1px solid #E5E7EB', minWidth:90 }}>EAC</th>}
                    {actVisibleCols.thisPeriod && <th style={{ padding:'9px 14px', textAlign:'right', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', borderBottom:'1px solid #E5E7EB', minWidth:90 }}>This Period</th>}
                    {actVisibleCols.varComp && <th style={{ padding:'9px 14px', textAlign:'right', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', borderBottom:'1px solid #E5E7EB', minWidth:100 }}>Var @ Comp</th>}
                    <th style={{ padding:'9px 14px', borderBottom:'1px solid #E5E7EB', minWidth:80 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredActivities.map((act, ai) => {
                    const row = computeActivityRow(act.costCodeIds)
                    const vc = varBadge(row.eac, row.bac)
                    // Uses the same flattenAllLevels() helper
                    // computeActivityRow() uses — previously this looked
                    // only at top-level Cost Code rows
                    // (reportData.directRows/indirectRows directly), so a
                    // Resource-mode Activity's selected Resource ids never
                    // matched anything here: no badge in the "Cost Codes"
                    // column, and expanding the row showed nothing, even
                    // though computeActivityRow()'s totals (which already
                    // used the shared helper) were correct.
                    const allNodes = flattenAllLevels([...(reportData.directRows||[]),...(reportData.indirectRows||[])])
                    const matched = allNodes.filter(r => act.costCodeIds.includes(r.id))
                    const isExpanded = expandedActivities.has(act.id)
                    return (
                      <>
                        <tr key={act.id} style={{ background: ai%2===0?'#fff':'#F9FAFB', borderBottom:'1px solid #E5E7EB', cursor:'pointer' }}
                          onClick={() => setExpandedActivities(prev => { const n=new Set(prev); n.has(act.id)?n.delete(act.id):n.add(act.id); return n })}>
                          <td style={{ padding:'10px 14px', fontWeight:700, color:'#0F1017' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                              <span style={{ fontSize:10, color:'#1A7A4A' }}>{isExpanded ? '▼' : '▶'}</span>
                              {act.name}
                            </div>
                          </td>
                          <td style={{ padding:'10px 14px' }}>
                            <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                              {matched.map(r => (
                                <span key={r.id} style={{ fontSize:10, fontWeight:600, padding:'2px 7px', borderRadius:10, background:'#F3F4F6', color:'#374151', fontFamily:'monospace' }}>{r.code}</span>
                              ))}
                            </div>
                          </td>
                          {actVisibleCols.bac && <td style={{ padding:'10px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:600 }}>{fmt(row.bac)}</td>}
                          {actVisibleCols.commitment && <td style={{ padding:'10px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmt(row.commitment)}</td>}
                          {actVisibleCols.actual && <td style={{ padding:'10px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:700 }}>{fmt(row.actual)}</td>}
                          {actVisibleCols.accrual && <td style={{ padding:'10px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', color:'#D97706' }}>{row.accrual > 0 ? fmt(row.accrual) : '—'}</td>}
                          {actVisibleCols.cashOut && <td style={{ padding:'10px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmt(row.cashOut)}</td>}
                          {actVisibleCols.eac && <td style={{ padding:'10px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:700, color: row.eac > row.bac ? '#DC2626' : '#1A7A4A' }}>{fmt(row.eac)}</td>}
                          {actVisibleCols.thisPeriod && <td style={{ padding:'10px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', color:'#6B7280' }}>{fmt(row.thisPeriod)}</td>}
                          {actVisibleCols.varComp && <td style={{ padding:'10px 14px', textAlign:'right' }}>
                            <span style={{ background:vc.bg, color:vc.color, borderRadius:4, padding:'2px 7px', fontWeight:600, fontSize:11 }}>{row.varComp >= 0 ? '+' : ''}{fmt(row.varComp)}</span>
                          </td>}
                          <td style={{ padding:'10px 14px' }} onClick={e => e.stopPropagation()}>
                            <div style={{ display:'flex', gap:4 }}>
                              {can('CostControl.Edit') && (
                                <button onClick={() => { setActForm({ id: act.id, name: act.name, costCodeIds: [...act.costCodeIds], analysisMode: act.analysisMode || 'cost_code' }); setActEditOpen(true) }}
                                  style={{ padding:'3px 8px', fontSize:10.5, border:'1px solid #E5E7EB', background:'#fff', color:'#6B7280', borderRadius:4, cursor:'pointer' }}>Edit</button>
                              )}
                              {can('CostControl.Edit') && (
                                <button onClick={async () => { if(window.confirm('Delete activity?')) await deleteActivity(act.id) }}
                                  style={{ padding:'3px 8px', fontSize:10.5, border:'1px solid #FECACA', background:'#FEF2F2', color:'#DC2626', borderRadius:4, cursor:'pointer' }}>Del</button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {/* Sub-rows: breakdown per cost code — collapsible */}
                        {isExpanded && matched.map((r, ri) => (
                          <tr key={r.id} style={{ background: ai%2===0?'#F9FAFB':'#F3F4F6', borderBottom:'1px solid #F0F0F0' }}>
                            <td style={{ padding:'6px 14px 6px 28px', color:'#6B7280', fontSize:11.5 }}>↳ {r.description}</td>
                            <td style={{ padding:'6px 14px', fontFamily:'monospace', fontSize:11, color:'#9CA3AF' }}>{r.code}</td>
                            {actVisibleCols.bac && <td style={{ padding:'6px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontSize:11.5, color:'#6B7280' }}>{fmt(r.bac)}</td>}
                            {actVisibleCols.commitment && <td style={{ padding:'6px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontSize:11.5, color:'#6B7280' }}>{fmt(r.commitment)}</td>}
                            {actVisibleCols.actual && <td style={{ padding:'6px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontSize:11.5, color:'#6B7280' }}>{fmt(r.totalActualCost)}</td>}
                            {actVisibleCols.accrual && <td style={{ padding:'6px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontSize:11.5, color:'#6B7280' }}>{fmt(r.accrual)}</td>}
                            {actVisibleCols.cashOut && <td style={{ padding:'6px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontSize:11.5, color:'#6B7280' }}>{fmt(r.cashOut)}</td>}
                            {actVisibleCols.eac && <td style={{ padding:'6px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontSize:11.5, color: r.eac > r.bac ? '#DC2626' : '#1A7A4A' }}>{fmt(r.eac)}</td>}
                            {actVisibleCols.thisPeriod && <td style={{ padding:'6px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontSize:11.5, color:'#6B7280' }}>{fmt(r.actualThisPeriod)}</td>}
                            {actVisibleCols.varComp && <td style={{ padding:'6px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontSize:11.5 }}>
                              <span style={{ color: (r.bac-r.eac) >= 0 ? '#1A7A4A' : '#DC2626', fontWeight:600 }}>{(r.bac-r.eac) >= 0 ? '+' : ''}{fmt(r.bac-r.eac)}</span>
                            </td>}
                            <td/>
                          </tr>
                        ))}
                      </>
                    )
                  })}
                </tbody>
                {/* Grand Totals footer */}
                <tfoot>
                  <tr style={{ background:'#0F1017', color:'#fff' }}>
                    <td style={{ padding:'10px 14px', fontWeight:700, fontSize:12, letterSpacing:'.04em' }}>TOTAL</td>
                    <td style={{ padding:'10px 14px', fontSize:11, color:'rgba(255,255,255,.45)' }}>{filteredActivities.length} activities</td>
                    {actVisibleCols.bac && <td style={{ padding:'10px 14px', textAlign:'right', fontWeight:700, fontVariantNumeric:'tabular-nums' }}>{fmt(grandTotal.bac)}</td>}
                    {actVisibleCols.commitment && <td style={{ padding:'10px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmt(grandTotal.commitment)}</td>}
                    {actVisibleCols.actual && <td style={{ padding:'10px 14px', textAlign:'right', fontWeight:700, fontVariantNumeric:'tabular-nums' }}>{fmt(grandTotal.actual)}</td>}
                    {actVisibleCols.accrual && <td style={{ padding:'10px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums', color:'#FBBF24' }}>{grandTotal.accrual > 0 ? fmt(grandTotal.accrual) : '—'}</td>}
                    {actVisibleCols.cashOut && <td style={{ padding:'10px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmt(grandTotal.cashOut)}</td>}
                    {actVisibleCols.eac && <td style={{ padding:'10px 14px', textAlign:'right', fontWeight:700, fontVariantNumeric:'tabular-nums', color: grandTotal.eac > grandTotal.bac ? '#F87171' : '#4ADE80' }}>{fmt(grandTotal.eac)}</td>}
                    {actVisibleCols.thisPeriod && <td style={{ padding:'10px 14px', textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmt(grandTotal.thisPeriod)}</td>}
                    {actVisibleCols.varComp && <td style={{ padding:'10px 14px', textAlign:'right' }}>
                      <span style={{ color: grandTotal.varComp >= 0 ? '#4ADE80' : '#F87171', fontWeight:700 }}>{grandTotal.varComp >= 0 ? '+' : ''}{fmt(grandTotal.varComp)}</span>
                    </td>}
                    <td/>
                  </tr>
                </tfoot>
              </table>
            </div>
            )
          })()}
        </div>
        )
      })()}

      {/* ── Activity Edit Modal ── */}
      {actEditOpen && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => setActEditOpen(false)}>
          <div style={{ background:'#fff', borderRadius:12, padding:28, width:520, maxHeight:'80vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,.3)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:15, fontWeight:700, color:'#0F1017', marginBottom:16 }}>{actForm.id ? 'Edit Activity' : 'New Activity'}</div>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:11, fontWeight:600, color:'#6B7280', textTransform:'uppercase', letterSpacing:'.07em', display:'block', marginBottom:5 }}>Activity Name</label>
              <input value={actForm.name} onChange={e => setActForm(p => ({...p, name: e.target.value}))}
                placeholder="e.g. Piles, Substructure, MEP..."
                style={{ width:'100%', padding:'8px 12px', border:'1px solid #D1D5DB', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }}/>
            </div>

            {/* Analysis Mode — Cost Code (Package View) vs Resource
                (Resource View) are two distinct analysis dimensions
                (explicitly agreed), not two levels of one selection.
                Locked once at least one code is selected, to prevent
                switching mid-edit from leaving stale ids from the other
                level in costCodeIds. */}
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:11, fontWeight:600, color:'#6B7280', textTransform:'uppercase', letterSpacing:'.07em', display:'block', marginBottom:5 }}>Analysis Mode</label>
              <div style={{ display:'flex', gap:6 }}>
                {[['cost_code','By Cost Code','Package view — aggregates every Resource & Sub Code beneath each selected Cost Code'],
                  ['resource','By Resource','Resource view — aggregates each selected Resource\'s own Sub Codes only']].map(([mode, label, hint]) => (
                  <button key={mode}
                    disabled={actForm.costCodeIds.length > 0 && actForm.analysisMode !== mode}
                    onClick={() => setActForm(p => ({ ...p, analysisMode: mode }))}
                    title={hint}
                    style={{ flex:1, padding:'8px 10px', borderRadius:8, fontSize:12, fontWeight:600, cursor: (actForm.costCodeIds.length > 0 && actForm.analysisMode !== mode) ? 'not-allowed' : 'pointer',
                      border: actForm.analysisMode === mode ? '1.5px solid #1A7A4A' : '1px solid #E5E7EB',
                      background: actForm.analysisMode === mode ? '#F0FFF4' : '#fff',
                      color: actForm.analysisMode === mode ? '#1A7A4A' : '#6B7280',
                      opacity: (actForm.costCodeIds.length > 0 && actForm.analysisMode !== mode) ? 0.4 : 1 }}>
                    {label}
                  </button>
                ))}
              </div>
              {actForm.costCodeIds.length > 0 && (
                <p style={{ fontSize:10.5, color:'#9CA3AF', marginTop:5 }}>Mode is locked while codes are selected below — clear the selection to switch.</p>
              )}
            </div>

            <div style={{ marginBottom:20 }}>
              <label style={{ fontSize:11, fontWeight:600, color:'#6B7280', textTransform:'uppercase', letterSpacing:'.07em', display:'block', marginBottom:5 }}>
                {actForm.analysisMode === 'resource' ? 'Select Resources' : 'Select Cost Codes'}
              </label>
              <div style={{ border:'1px solid #E5E7EB', borderRadius:8, maxHeight:280, overflow:'auto' }}>
                {/* Each mode lists ONLY its own level — Cost Codes for
                    'cost_code' mode, Resources (flattened across every
                    Cost Code) for 'resource' mode. Never both in the same
                    list, so a mixed/ambiguous selection is structurally
                    impossible rather than merely discouraged. */}
                {(actForm.analysisMode === 'resource'
                  ? flattenAllLevels([...(reportData.directRows||[]),...(reportData.indirectRows||[])]).filter(r => r.isResource)
                  : [...(reportData.directRows||[]),...(reportData.indirectRows||[])]
                ).map(r => (
                  <div key={r.id} onClick={() => setActForm(p => ({ ...p, costCodeIds: p.costCodeIds.includes(r.id) ? p.costCodeIds.filter(x=>x!==r.id) : [...p.costCodeIds, r.id] }))}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', cursor:'pointer', borderBottom:'1px solid #F3F4F6',
                      background: actForm.costCodeIds.includes(r.id) ? '#F0FFF4' : '#fff' }}>
                    <div style={{ width:16, height:16, borderRadius:3, border: actForm.costCodeIds.includes(r.id) ? 'none' : '1.5px solid #D1D5DB',
                      background: actForm.costCodeIds.includes(r.id) ? '#1A7A4A' : 'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      {actForm.costCodeIds.includes(r.id) && <span style={{ color:'#fff', fontSize:11, lineHeight:1 }}>✓</span>}
                    </div>
                    <span style={{ fontFamily:'monospace', fontSize:12, color:'#6B7280', minWidth:80 }}>{r.code}</span>
                    <span style={{ fontSize:12, color:'#374151' }}>{r.description}</span>
                    <span style={{ marginLeft:'auto', fontSize:11, color:'#9CA3AF' }}>{fmtK(r.bac)}</span>
                  </div>
                ))}
              </div>
              {actForm.costCodeIds.length > 0 && (
                <div style={{ marginTop:8, fontSize:11, color:'#6B7280' }}>{actForm.costCodeIds.length} {actForm.analysisMode === 'resource' ? 'resource(s)' : 'cost code(s)'} selected</div>
              )}
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={() => setActEditOpen(false)}
                style={{ padding:'8px 16px', border:'1px solid #E5E7EB', borderRadius:8, background:'#fff', fontSize:12, cursor:'pointer' }}>Cancel</button>
              {can('CostControl.Edit') && (
                <button onClick={async () => {
                if (!actForm.name.trim()) { alert('Enter activity name'); return }
                if (!actForm.costCodeIds.length) { alert('Select at least one cost code'); return }
                await saveActivity(actForm)
                setActEditOpen(false)
              }}
                style={{ padding:'8px 20px', borderRadius:8, background:'#1A7A4A', color:'#fff', border:'none', fontSize:12, fontWeight:600, cursor:'pointer' }}>Save</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Drill Modal ── */}
      {drillRow && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => setDrillRow(null)}>
          <div style={{ background:'#fff', borderRadius:12, padding:32, width:'90vw', maxWidth:1100, maxHeight:'90vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
              <div style={{ fontSize:16, fontWeight:700, color:'#0F1017' }}>
                <span style={{ fontFamily:'monospace', background:'#F3F4F6', padding:'2px 8px', borderRadius:4, marginRight:8 }}>{drillRow.code}</span>
                {drillRow.description}
              </div>
              <button onClick={() => setDrillRow(null)} style={{ border:'none', background:'none', fontSize:20, color:'#9CA3AF', cursor:'pointer', lineHeight:1 }}>×</button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:20 }}>
              {[
                { label:'BAC', value: fmt(drillRow.bac), color:'#0F1017' },
                { label:'Commitment', value: fmt(drillRow.commitment), color:'#374151' },
                { label:'Accrual', value: drillRow.accrual > 0 ? fmt(drillRow.accrual) : '—', color:'#D97706' },
                { label:'Total Actual', value: fmt(drillRow.totalActualCost), color:'#0F1017' },
                { label:'Cash Out', value: fmt(drillRow.cashOut), color:'#374151' },
                { label:'EAC', value: fmt(drillRow.eac), color: drillRow.eac > drillRow.bac ? '#DC2626' : '#1A7A4A' },
              ].map(s => (
                <div key={s.label} style={{ background:'#F9FAFB', borderRadius:8, padding:'10px 14px' }}>
                  <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', marginBottom:4 }}>{s.label}</div>
                  <div style={{ fontSize:15, fontWeight:700, color: s.color }}>{s.value}</div>
                </div>
              ))}
            </div>
            {(drillRow.children || []).length > 0 && (
              <>
                <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', marginBottom:8 }}>Sub Codes</div>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr style={{ background:'#F9FAFB' }}>
                      {['Sub Code','Description','BAC','Actual','EAC','Var@Comp'].map(h => (
                        <th key={h} style={{ padding:'7px 10px', textAlign:['BAC','Actual','EAC','Var@Comp'].includes(h)?'right':'left', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#6B7280', borderBottom:'1px solid #E5E7EB' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {drillRow.children.map((c, i) => {
                      const vc = varBadge(c.eac, c.bac)
                      return (
                        <tr key={c.id} style={{ background: i%2===0?'#fff':'#F9FAFB', borderBottom:'1px solid #F3F4F6' }}>
                          <td style={{ padding:'7px 10px', fontFamily:'monospace', fontSize:11, color:'#6B7280' }}>{c.code}</td>
                          <td style={{ padding:'7px 10px', color:'#374151', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.description}</td>
                          <td style={{ padding:'7px 10px', textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmt(c.bac)}</td>
                          <td style={{ padding:'7px 10px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:600 }}>{fmt(c.totalActualCost)}</td>
                          <td style={{ padding:'7px 10px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:700, color: c.eac > c.bac ? '#DC2626' : '#1A7A4A' }}>{fmt(c.eac)}</td>
                          <td style={{ padding:'7px 10px', textAlign:'right' }}>
                            <span style={{ background:vc.bg, color:vc.color, borderRadius:4, padding:'2px 6px', fontSize:11, fontWeight:600 }}>
                              {c.eac <= c.bac ? '+' : ''}{fmt(c.bac - c.eac)}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}

      {/* Set Actual Cost Period — simple modal replacing the old Month/Year dropdowns */}
      {showPeriodModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:100 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:24, width:320, boxShadow:'0 20px 60px rgba(0,0,0,.25)' }}>
            <div style={{ fontSize:14, fontWeight:700, color:'#0F1017', marginBottom:4 }}>Set Actual Cost Period</div>
            <div style={{ fontSize:11.5, color:'#6B7280', marginBottom:16 }}>يحدد الشهر الظاهر تحت عمود Actual Cost</div>
            <div style={{ display:'flex', gap:8, marginBottom:20 }}>
              <select value={selectedMonth} onChange={e => setSelectedMonth(parseInt(e.target.value))}
                style={{ flex:1, padding:'8px 10px', border:'1px solid #D1D5DB', borderRadius:8, fontSize:13, background:'#fff', cursor:'pointer' }}>
                {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
              </select>
              <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))}
                style={{ flex:1, padding:'8px 10px', border:'1px solid #D1D5DB', borderRadius:8, fontSize:13, background:'#fff', cursor:'pointer' }}>
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => setShowPeriodModal(false)}
                style={{ flex:1, padding:'8px 16px', border:'1px solid #E5E7EB', borderRadius:8, background:'#fff', fontSize:12.5, cursor:'pointer' }}>Cancel</button>
              {can('CostControl.Edit') && (
                <button onClick={async () => {
                setShowPeriodModal(false)
                await supabase.from('projects').update({
                  cost_control_selected_month: selectedMonth,
                  cost_control_selected_year: selectedYear,
                }).eq('id', projectId)
                buildReport()
              }}
                style={{ flex:1, padding:'8px 16px', borderRadius:8, background:'#1A7A4A', color:'#fff', border:'none', fontSize:12.5, fontWeight:600, cursor:'pointer' }}>Apply</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================
// Summary View — Direct Works totals grouped by Resource Type
// (Material / Labour / Subcontract / Equipment), plus Indirect
// as a single line, Grand Total, and Profit (Contract Value − Total EAC).
// ============================================================
function SummaryView({ reportData, contractValue, fmt, monthlyActualCost, visibleCols }) {
  const rt = reportData.resourceTypeTotals || {}
  const indirect = reportData.indirectTotals || {}
  const hasCategories = (reportData.indirectCategories || []).length > 0
  const rows = [
    { key: 'material', label: 'Material', color: '#2563EB' },
    { key: 'labour', label: 'Labour', color: '#16A34A' },
    { key: 'subcontract', label: 'Subcontract', color: '#DC2626' },
    { key: 'equipment', label: 'Equipment', color: '#D97706' },
  ].map(r => ({ ...r, data: rt[r.key] || {} }))

  const directGrand = rows.reduce((acc, r) => {
    for (const k of ['bac','commitment','eac','totalActualCost','accrual','earnedValue']) acc[k] = (acc[k]||0) + (r.data[k]||0)
    return acc
  }, {})
  directGrand.cpi = (directGrand.earnedValue && directGrand.totalActualCost) ? (directGrand.earnedValue / directGrand.totalActualCost) : 0

  // Grand Total (whole project, Direct + Indirect) — read directly from
  // reportData.grandTotals, the exact same SSOT figure buildCumulativeData()
  // already computes and every other consumer (Header KPIs, Excel export,
  // Activity Reports) relies on. Previously this view rebuilt its own Grand
  // Total by summing resourceTypeTotals + indirectTotals, which silently
  // excluded any Cost Code carrying a direct Forecast Override on the Cost
  // Code itself (an EAC resolved directly on the Cost Code, not rolled up
  // from its Resource Codes) — that value lives in grandTotals.eac but was
  // never included in resourceTypeTotals (which only sums Resource Code
  // rows). Reading grandTotals directly removes that gap entirely: this is
  // now the same number shown everywhere else in the app, not a
  // locally-recomputed approximation of it.
  const gt = reportData.grandTotals || {}
  const grandBac = gt.bac || 0
  const grandEac = gt.eac || 0
  const grandCommitment = gt.commitment || 0
  const grandActual = gt.totalActualCost || 0
  const grandAccrual = gt.accrual || 0
  const grandEv = gt.earnedValue || 0
  const grandCpi = gt.cpi || 0
  const grandEtc = grandEac - grandActual
  const grandVac = grandBac - grandEac
  // Cost Variance for the Grand Total row -- same formula used everywhere
  // else (Row component, PDF model): EV - Actual Cost. Was previously
  // missing entirely from this row, which caused every column after
  // Earned Value to render without its matching footer cell (no visual
  // slot reserved for Cost Variance), making the CPI value appear to sit
  // under the "Cost Variance" header instead of its own.
  const grandCostVariance = grandEv - grandActual

  // This Period (Movement) — read from monthlyActualCost, the same
  // Movement source Executive Report and Cost Control PDF already share.
  // No new financial calculation: every number below is a lookup into
  // monthlyActualCost.byResourceCode/.byCostCode (already computed by
  // buildMonthlyActualCostData()), grouped by the SAME resource_type /
  // Indirect Category classification reportData itself already carries
  // (identical grouping to resourceTypeTotals/directTotals/cat.totals
  // above — just summing a different pre-computed field, Movement,
  // instead of eac/bac/actualCost).
  function movementForResourceType(type) {
    if (!monthlyActualCost) return 0
    return (reportData.directRows || [])
      .flatMap(parent => (parent.resourceRows || []).filter(r => r.resource_type === type))
      .reduce((s, res) => s + (monthlyActualCost.byResourceCode?.[res.code]?.movement ?? monthlyActualCost.bySubCode?.[res.code]?.movement ?? monthlyActualCost.byCostCode?.[res.code]?.movement ?? 0), 0)
  }
  function movementForIndirectRows(indirectRows) {
    if (!monthlyActualCost) return 0
    return (indirectRows || []).reduce((s, parent) =>
      s + (monthlyActualCost.byCostCode?.[parent.code]?.movement ?? monthlyActualCost.bySubCode?.[parent.code]?.movement ?? 0), 0)
  }
  const movementByType = { material: movementForResourceType('material'), labour: movementForResourceType('labour'), subcontract: movementForResourceType('subcontract'), equipment: movementForResourceType('equipment') }
  const directMovement = Object.values(movementByType).reduce((s, v) => s + v, 0)
  const indirectMovement = hasCategories
    ? (reportData.indirectCategories || []).reduce((s, cat) => s + movementForIndirectRows(cat.rows || []), 0) + movementForIndirectRows(reportData.uncategorizedIndirectRows || [])
    : movementForIndirectRows(reportData.indirectRows || [])
  const grandMovement = monthlyActualCost?.grandMovement ?? (directMovement + indirectMovement)

  // Current (as of today) profit — based on EAC
  const profit = (contractValue || 0) - grandEac
  const profitPct = contractValue ? (profit / contractValue * 100) : 0
  // Original profit — based on the original Budget (BAC), fixed regardless of current forecast
  const originalProfit = (contractValue || 0) - grandBac
  const originalProfitPct = contractValue ? (originalProfit / contractValue * 100) : 0

  const showCommitment = visibleCols ? visibleCols.commitment !== false : true
  const showAccrual = visibleCols ? visibleCols.accrual !== false : true
  const showEv = visibleCols ? visibleCols.ev === true : false
  const showCpi = visibleCols ? visibleCols.cpi === true : false
  const showCostVariance = visibleCols ? visibleCols.costVariance === true : false

  const Row = ({ label, color, data, bold, movement }) => {
    const etc = (data.eac||0) - (data.totalActualCost||0)
    const vac = (data.bac||0) - (data.eac||0)
    const cpi = data.cpi ?? ((data.earnedValue && data.totalActualCost) ? (data.earnedValue / data.totalActualCost) : 0)
    const costVariance = (data.earnedValue || 0) - (data.totalActualCost || 0)
    return (
    <tr style={{ borderBottom:'1px solid #D1D5DB', background: bold ? '#E8EDF3' : '#fff' }}>
      <td style={{ padding:'10px 16px', fontWeight: bold ? 700 : 600, color: bold ? '#0F1017' : '#374151', borderRight:'1px solid #D1D5DB' }}>
        {color && <span style={{ display:'inline-block', width:8, height:8, borderRadius:2, background:color, marginRight:8 }}/>}
        {label}
      </td>
      <td style={{ padding:'10px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', borderRight:'1px solid #D1D5DB' }}>{fmt(data.bac)}</td>
      {showCommitment && <td style={{ padding:'10px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', borderRight:'1px solid #D1D5DB' }}>{fmt(data.commitment)}</td>}
      {showAccrual && <td style={{ padding:'10px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', color:'#D97706', borderRight:'1px solid #D1D5DB' }}>{data.accrual > 0 ? fmt(data.accrual) : '—'}</td>}
      <td style={{ padding:'10px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', borderRight:'1px solid #D1D5DB' }}>{fmt(data.totalActualCost)}</td>
      <td style={{ padding:'10px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', color: (movement||0) >= 0 ? '#1A7A4A' : '#DC2626', borderRight:'1px solid #D1D5DB' }}>{monthlyActualCost ? ((movement>=0?'+':'')+fmt(movement)) : '—'}</td>
      <td style={{ padding:'10px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', color:'#6B7280', borderRight:'1px solid #D1D5DB' }}>{fmt(etc)}</td>
      <td style={{ padding:'10px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:700, color: (data.eac||0) > (data.bac||0) ? '#DC2626' : '#1A7A4A', borderRight:'1px solid #D1D5DB' }}>{fmt(data.eac)}</td>
      <td style={{ padding:'10px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:600, color: vac >= 0 ? '#1A7A4A' : '#DC2626', borderRight:'1px solid #D1D5DB' }}>{vac>=0?'+':''}{fmt(vac)}</td>
      {showEv && <td style={{ padding:'10px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', color:'#7C3AED', borderRight:'1px solid #D1D5DB' }}>{data.earnedValue > 0 ? fmt(data.earnedValue) : '—'}</td>}
      {showCostVariance && <td style={{ padding:'10px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:600, color: costVariance >= 0 ? '#1A7A4A' : '#DC2626', borderRight:'1px solid #D1D5DB' }}>{(costVariance>=0?'+':'')+fmt(costVariance)}</td>}
      {showCpi && <td style={{ padding:'10px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:600, color: !cpi ? '#9CA3AF' : cpi >= 1 ? '#1A7A4A' : '#DC2626' }}>{cpi ? cpi.toFixed(2) : '—'}</td>}
    </tr>
    )
  }

  return (
    <div style={{ flex:1, overflow:'auto', margin:'0 24px 16px' }}>
      <div style={{ border:'1.5px solid #0F1017', borderRadius:8, background:'#fff', overflow:'hidden' }}>
        {/* Title bar — matches the reference "Summary" banner while keeping our own styling language */}
        <div style={{ padding:'10px 16px', textAlign:'center', fontWeight:700, fontSize:13.5, color:'#0F1017', borderBottom:'1.5px solid #0F1017', background:'#fff' }}>
          Summary
        </div>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:'#DCE3EC', borderBottom:'1.5px solid #0F1017' }}>
              <th style={{ padding:'10px 16px', textAlign:'left', fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#374151', borderRight:'1px solid #D1D5DB' }}>Category</th>
              <th style={{ padding:'10px 16px', textAlign:'right', fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#374151', borderRight:'1px solid #D1D5DB' }}>BAC</th>
              {showCommitment && <th style={{ padding:'10px 16px', textAlign:'right', fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#374151', borderRight:'1px solid #D1D5DB' }}>Commitment</th>}
              {showAccrual && <th style={{ padding:'10px 16px', textAlign:'right', fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#374151', borderRight:'1px solid #D1D5DB' }}>Accrual</th>}
              <th style={{ padding:'10px 16px', textAlign:'right', fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#374151', borderRight:'1px solid #D1D5DB' }}>Actual Cost</th>
              <th style={{ padding:'10px 16px', textAlign:'right', fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#374151', borderRight:'1px solid #D1D5DB' }}>This Period</th>
              <th style={{ padding:'10px 16px', textAlign:'right', fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#374151', borderRight:'1px solid #D1D5DB' }}>ETC</th>
              <th style={{ padding:'10px 16px', textAlign:'right', fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#374151', borderRight:'1px solid #D1D5DB' }}>EAC</th>
              <th style={{ padding:'10px 16px', textAlign:'right', fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#374151', borderRight:'1px solid #D1D5DB' }}>Var @ Comp</th>
              {showEv && <th style={{ padding:'10px 16px', textAlign:'right', fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#374151', borderRight:'1px solid #D1D5DB' }}>Earned Value</th>}
              {showCostVariance && <th style={{ padding:'10px 16px', textAlign:'right', fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#374151', borderRight:'1px solid #D1D5DB' }}>Cost Variance</th>}
              {showCpi && <th style={{ padding:'10px 16px', textAlign:'right', fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#374151' }}>CPI</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => <Row key={r.key} label={r.label} color={r.color} data={r.data} movement={movementByType[r.key]} />)}
            <Row label="Direct Works Total" data={directGrand} bold movement={directMovement} />
            {hasCategories ? (
              <>
                {(reportData.indirectCategories || []).map(cat => (
                  <Row key={cat.id} label={cat.name} color="#7C3AED" data={cat.totals} movement={movementForIndirectRows(cat.rows || [])} />
                ))}
                {(reportData.uncategorizedIndirectRows || []).length > 0 && (
                  <Row label="Uncategorized" color="#9CA3AF" data={reportData.uncategorizedTotals} movement={movementForIndirectRows(reportData.uncategorizedIndirectRows)} />
                )}
              </>
            ) : (
              <Row label="Indirect Works" color="#7C3AED" data={indirect} movement={indirectMovement} />
            )}
            <tr style={{ background:'#0F1017' }}>
              <td style={{ padding:'12px 16px', fontWeight:800, color:'#fff', letterSpacing:'.03em', borderRight:'1px solid rgba(255,255,255,.15)' }}>GRAND TOTAL</td>
              <td style={{ padding:'12px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', color:'#fff', fontWeight:700, borderRight:'1px solid rgba(255,255,255,.15)' }}>{fmt(grandBac)}</td>
              {showCommitment && <td style={{ padding:'12px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', color:'#fff', fontWeight:700, borderRight:'1px solid rgba(255,255,255,.15)' }}>{fmt(grandCommitment)}</td>}
              {showAccrual && <td style={{ padding:'12px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', color:'#FCD34D', fontWeight:700, borderRight:'1px solid rgba(255,255,255,.15)' }}>{grandAccrual > 0 ? fmt(grandAccrual) : '—'}</td>}
              <td style={{ padding:'12px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', color:'#fff', fontWeight:700, borderRight:'1px solid rgba(255,255,255,.15)' }}>{fmt(grandActual)}</td>
              <td style={{ padding:'12px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:700, color: (grandMovement||0) >= 0 ? '#4ADE80' : '#F87171', borderRight:'1px solid rgba(255,255,255,.15)' }}>{monthlyActualCost ? ((grandMovement>=0?'+':'')+fmt(grandMovement)) : '—'}</td>
              <td style={{ padding:'12px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', color:'rgba(255,255,255,.75)', borderRight:'1px solid rgba(255,255,255,.15)' }}>{fmt(grandEtc)}</td>
              <td style={{ padding:'12px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', color:'#fff', fontWeight:800, borderRight:'1px solid rgba(255,255,255,.15)' }}>{fmt(grandEac)}</td>
              <td style={{ padding:'12px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:700, color: grandVac >= 0 ? '#4ADE80' : '#F87171', borderRight:'1px solid rgba(255,255,255,.15)' }}>{grandVac>=0?'+':''}{fmt(grandVac)}</td>
              {showEv && <td style={{ padding:'12px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', color:'#C4B5FD', fontWeight:700, borderRight:'1px solid rgba(255,255,255,.15)' }}>{grandEv > 0 ? fmt(grandEv) : '—'}</td>}
              {showCostVariance && <td style={{ padding:'12px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:700, color: grandCostVariance >= 0 ? '#4ADE80' : '#F87171', borderRight:'1px solid rgba(255,255,255,.15)' }}>{(grandCostVariance>=0?'+':'')+fmt(grandCostVariance)}</td>}
              {showCpi && <td style={{ padding:'12px 16px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:700, color: !grandCpi ? 'rgba(255,255,255,.5)' : grandCpi >= 1 ? '#4ADE80' : '#FCA5A5' }}>{grandCpi ? grandCpi.toFixed(2) : '—'}</td>}
            </tr>
          </tbody>
        </table>

        {contractValue > 0 && (
          <div style={{ borderTop:'1.5px solid #0F1017' }}>
            {/* Row 1: Contract Value spans full width */}
            <div style={{ padding:'14px 20px', borderBottom:'1px solid #D1D5DB', background:'#F9FAFB' }}>
              <div style={{ fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#6B7280', marginBottom:4 }}>Contract Value</div>
              <div style={{ fontSize:20, fontWeight:800, color:'#0F1017' }}>{fmt(contractValue)}</div>
            </div>
            {/* Row 2: Original figures — aligned under Budget column conceptually (fixed, from BAC) */}
            <div style={{ display:'flex', gap:0, borderBottom:'1px solid #D1D5DB' }}>
              <div style={{ flex:1, padding:'14px 20px', borderRight:'1px solid #D1D5DB' }}>
                <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#9CA3AF', marginBottom:4 }}>Original Gross Profit <span style={{fontWeight:400,textTransform:'none'}}>(Contract − Budget)</span></div>
                <div style={{ fontSize:18, fontWeight:800, color: originalProfit >= 0 ? '#1A7A4A' : '#DC2626' }}>{fmt(originalProfit)}</div>
              </div>
              <div style={{ flex:1, padding:'14px 20px' }}>
                <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#9CA3AF', marginBottom:4 }}>Original GP %</div>
                <div style={{ fontSize:18, fontWeight:800, color: originalProfit >= 0 ? '#1A7A4A' : '#DC2626' }}>{originalProfitPct.toFixed(1)}%</div>
              </div>
            </div>
            {/* Row 3: Current figures — aligned under EAC column conceptually */}
            <div style={{ display:'flex', gap:0 }}>
              <div style={{ flex:1, padding:'16px 20px', borderRight:'1px solid #D1D5DB' }}>
                <div style={{ fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#6B7280', marginBottom:4 }}>Gross Profit <span style={{fontWeight:400,textTransform:'none'}}>(Current, based on EAC)</span></div>
                <div style={{ fontSize:20, fontWeight:800, color: profit >= 0 ? '#1A7A4A' : '#DC2626' }}>{fmt(profit)}</div>
              </div>
              <div style={{ flex:1, padding:'16px 20px' }}>
                <div style={{ fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#6B7280', marginBottom:4 }}>GP %</div>
                <div style={{ fontSize:20, fontWeight:800, color: profit >= 0 ? '#1A7A4A' : '#DC2626' }}>{profitPct.toFixed(1)}%</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}